<template>
  <div class="city-page" :class="{ 'edit-mode': editMode }">
    <AppTopHeader highlight-discover />

    <div v-if="editMode" class="admin-edit-bar">
      <span>{{ $t('city.editBar.label') }}</span>
      <div class="admin-edit-bar-actions">
        <button type="button" class="admin-btn-leave" :disabled="saving" @click="exitEditMode">{{ $t('city.editBar.leave') }}</button>
        <button type="button" class="admin-btn-save" :disabled="saving || !city" @click="saveDestinationAdmin">
          {{ saving ? $t('city.editBar.saving') : $t('city.editBar.save') }}
        </button>
      </div>
    </div>

    <main v-if="loading" class="city-skeleton-root"><CityPageSkeleton /></main>
    <main v-else-if="!city" class="city-container city-loading">{{ $t('city.state.notFound') }}</main>

    <template v-else>
      <header class="city-hero-wrap">
        <div class="city-container hero-head">
          <div class="hero-titles">
            <div class="ep-anchor">
              <p class="country-line">
                <span class="material-symbols-outlined country-pin">location_on</span>
                <span class="country-upper">{{ city.country || "—" }}</span>
              </p>
              <button v-if="editMode" type="button" class="ep-btn" @click="startEdit('country')">{{ $t('city.hero.editCountry') }}</button>
            </div>
            <div class="ep-anchor title-row">
              <h1 class="city-title">{{ city.name }}</h1>
              <button v-if="editMode" type="button" class="ep-btn" @click="startEdit('name')">{{ $t('city.hero.editName') }}</button>
            </div>
            <div class="tags-row">
              <span v-for="(tag, ti) in city.tags" :key="`tag-${ti}`" class="tag-pill">{{ tag }}</span>
              <button v-if="editMode" type="button" class="ep-btn tag-edit" @click="startEdit('tags')">{{ $t('city.hero.editTags') }}</button>
            </div>
          </div>
          <div class="score-box">
            <div class="score-num">{{ formatScore(city.scoreOverall) }}</div>
            <div class="score-label">{{ $t('city.hero.scoreLabel') }}</div>
          </div>
        </div>

        <div class="city-container hero-gallery" v-if="hasAnyGalleryImage || editMode">
          <template v-for="(img, gi) in gallerySlots" :key="`g-${gi}`">
            <div
              v-if="img || editMode"
              class="gallery-cell"
              :class="[galleryClass(gi), { 'g-full': totalGalleryImages === 1 && !editMode }]"
            >
              <div class="ep-img-wrap">
                <img v-if="img" :src="img" :alt="city.name" class="gallery-img" />
                <div v-else class="gallery-placeholder">{{ $t('city.hero.noImage') }}</div>
                <button
                  v-if="editMode"
                  type="button"
                  class="ep-img-btn"
                  @click.stop="startEditHero(gi)"
                >
                  {{ $t('city.hero.editHeroUrl') }}
                </button>
              </div>
            </div>
          </template>
        </div>

        <div v-if="city.heroVibeLine || editMode" class="city-container vibe-row">
          <div class="vibe-card">
            <div class="vibe-label">{{ $t('city.hero.vibeLabel') }}</div>
            <div class="vibe-text ep-anchor">
              <span class="material-symbols-outlined vibe-sun">wb_sunny</span>
              <span>{{ city.heroVibeLine || "—" }}</span>
              <button v-if="editMode" type="button" class="ep-btn" @click="startEdit('heroVibeLine')">{{ $t('city.hero.editVibe') }}</button>
            </div>
          </div>
        </div>
      </header>

      <!-- Mobile Sticky Navigation Tabs -->
      <div class="mobile-sticky-nav scroll-hide" v-if="city">
        <div class="mobile-nav-scroll">
          <a
            href="#scores"
            :class="{ 'is-active': activeNavId === 'scores' }"
            @click.prevent="scrollTo('scores')"
          >{{ $t('city.nav.scores') }}</a>
          <a
            href="#costs"
            :class="{ 'is-active': activeNavId === 'costs' }"
            @click.prevent="scrollTo('costs')"
          >{{ $t('city.nav.costs') }}</a>
          <a
            href="#insights"
            :class="{ 'is-active': activeNavId === 'insights' }"
            @click.prevent="scrollTo('insights')"
          >{{ $t('city.nav.insights') }}</a>
          <a
            v-if="!isKsamil"
            href="#neighborhoods"
            :class="{ 'is-active': activeNavId === 'neighborhoods' }"
            @click.prevent="scrollTo('neighborhoods')"
          >{{ $t('city.nav.neighborhoods') }}</a>
          <a
            href="#climate"
            :class="{ 'is-active': activeNavId === 'climate' }"
            @click.prevent="scrollTo('climate')"
          >{{ $t('city.nav.climate') }}</a>
          <a
            v-if="!isKsamil && navShowApps"
            href="#essential-apps"
            :class="{ 'is-active': activeNavId === 'essential-apps' }"
            @click.prevent="scrollTo('essential-apps')"
          >{{ $t('city.nav.apps') }}</a>
          <a
            v-if="!isKsamil && navShowVisa"
            href="#visa"
            :class="{ 'is-active': activeNavId === 'visa' }"
            @click.prevent="scrollTo('visa')"
          >{{ $t('city.nav.visa') }}</a>

          <!-- Ksamil custom navigation links -->
          <template v-if="isKsamil">
            <a
              href="#arrival"
              :class="{ 'is-active': activeNavId === 'arrival' }"
              @click.prevent="scrollTo('arrival')"
            >{{ currentLang === 'pt-BR' ? 'Como Chegar' : 'How to Get Here' }}</a>
            <a
              href="#logistics"
              :class="{ 'is-active': activeNavId === 'logistics' }"
              @click.prevent="scrollTo('logistics')"
            >{{ currentLang === 'pt-BR' ? 'Logística & Finanças' : 'Logistics & Cash' }}</a>
            <a
              href="#mobility"
              :class="{ 'is-active': activeNavId === 'mobility' }"
              @click.prevent="scrollTo('mobility')"
            >{{ currentLang === 'pt-BR' ? 'Mobilidade' : 'Mobility' }}</a>
            <a
              href="#leisure"
              :class="{ 'is-active': activeNavId === 'leisure' }"
              @click.prevent="scrollTo('leisure')"
            >{{ currentLang === 'pt-BR' ? 'Praias & Lazer' : 'Beaches & Leisure' }}</a>
          </template>

          <template v-if="!isKsamil && navShowCustomSections">
            <a
              v-for="section in city.customSections"
              :key="section.anchorSlug"
              :href="`#${section.anchorSlug}`"
              :class="{ 'is-active': activeNavId === section.anchorSlug }"
              @click.prevent="scrollTo(section.anchorSlug)"
            >{{ section.title }}</a>
          </template>
        </div>
      </div>

      <div class="city-container city-layout">
        <aside class="city-sidebar">
          <div class="side-card">
            <h3 class="side-title">{{ $t('city.nav.title') }}</h3>
            <ul class="side-nav">
              <li>
                <a
                  href="#scores"
                  :class="{ 'is-active': activeNavId === 'scores' }"
                  @click.prevent="scrollTo('scores')"
                ><span class="material-symbols-outlined">analytics</span> {{ $t('city.nav.scores') }}</a>
              </li>
              <li>
                <a
                  href="#costs"
                  :class="{ 'is-active': activeNavId === 'costs' }"
                  @click.prevent="scrollTo('costs')"
                ><span class="material-symbols-outlined">payments</span> {{ $t('city.nav.costs') }}</a>
              </li>
              <li>
                <a
                  href="#insights"
                  :class="{ 'is-active': activeNavId === 'insights' }"
                  @click.prevent="scrollTo('insights')"
                ><span class="material-symbols-outlined">lightbulb</span> {{ $t('city.nav.insights') }}</a>
              </li>
              <li v-if="!isKsamil">
                <a
                  href="#neighborhoods"
                  :class="{ 'is-active': activeNavId === 'neighborhoods' }"
                  @click.prevent="scrollTo('neighborhoods')"
                ><span class="material-symbols-outlined">map</span> {{ $t('city.nav.neighborhoods') }}</a>
              </li>
              <li>
                <a
                  href="#climate"
                  :class="{ 'is-active': activeNavId === 'climate' }"
                  @click.prevent="scrollTo('climate')"
                ><span class="material-symbols-outlined">thermostat</span> {{ $t('city.nav.climate') }}</a>
              </li>
              <li v-if="!isKsamil && navShowApps">
                <a
                  href="#essential-apps"
                  :class="{ 'is-active': activeNavId === 'essential-apps' }"
                  @click.prevent="scrollTo('essential-apps')"
                ><span class="material-symbols-outlined">apps</span> {{ $t('city.nav.apps') }}</a>
              </li>
              <li v-if="!isKsamil && navShowVisa">
                <a
                  href="#visa"
                  :class="{ 'is-active': activeNavId === 'visa' }"
                  @click.prevent="scrollTo('visa')"
                ><span class="material-symbols-outlined">badge</span> {{ $t('city.nav.visa') }}</a>
              </li>

              <!-- Ksamil custom navigation links -->
              <template v-if="isKsamil">
                <li>
                  <a
                    href="#arrival"
                    :class="{ 'is-active': activeNavId === 'arrival' }"
                    @click.prevent="scrollTo('arrival')"
                  ><span class="material-symbols-outlined">route</span> {{ currentLang === 'pt-BR' ? 'Como Chegar' : 'How to Get Here' }}</a>
                </li>
                <li>
                  <a
                    href="#logistics"
                    :class="{ 'is-active': activeNavId === 'logistics' }"
                    @click.prevent="scrollTo('logistics')"
                  ><span class="material-symbols-outlined">payments</span> {{ currentLang === 'pt-BR' ? 'Logística & Finanças' : 'Logistics & Cash' }}</a>
                </li>
                <li>
                  <a
                    href="#mobility"
                    :class="{ 'is-active': activeNavId === 'mobility' }"
                    @click.prevent="scrollTo('mobility')"
                  ><span class="material-symbols-outlined">directions_car</span> {{ currentLang === 'pt-BR' ? 'Mobilidade' : 'Mobility' }}</a>
                </li>
                <li>
                  <a
                    href="#leisure"
                    :class="{ 'is-active': activeNavId === 'leisure' }"
                    @click.prevent="scrollTo('leisure')"
                  ><span class="material-symbols-outlined">umbrella</span> {{ currentLang === 'pt-BR' ? 'Praias & Lazer' : 'Beaches & Leisure' }}</a>
                </li>
              </template>

              <template v-if="!isKsamil && navShowCustomSections">
                <li v-for="section in city.customSections" :key="section.anchorSlug">
                  <a
                    :href="`#${section.anchorSlug}`"
                    :class="{ 'is-active': activeNavId === section.anchorSlug }"
                    @click.prevent="scrollTo(section.anchorSlug)"
                  ><span class="material-symbols-outlined">article</span> {{ section.title }}</a>
                </li>
              </template>
            </ul>
          </div>
        </aside>

        <div class="city-main">
          <section id="scores" class="section-block scroll-target">
            <h2 class="section-title">{{ $t('city.scores.title') }}</h2>
            <div class="vital-card">
              <div v-for="(st, si) in city.vitalStats" :key="`vs-${si}`" class="vital-row ep-anchor">
                <div class="vital-head">
                  <span>{{ st.label }}</span>
                  <span>{{ statDisplay(st) }}</span>
                </div>
                <div class="vital-bar-bg">
                  <div class="vital-bar-fill" :style="{ width: barWidth(st) + '%' }" />
                </div>
                <button v-if="editMode" type="button" class="ep-btn" @click="startEditVital(si)">{{ $t('city.admin.editBtn') }}</button>
              </div>
            </div>
          </section>

          <section id="costs" class="section-block scroll-target">
            <div class="section-head">
              <h2 class="section-title">{{ $t('city.costs.title') }}</h2>
              <span class="currency-pill">{{ $t('city.costs.currency') }}</span>
            </div>
            <div class="budget-grid">
              <div v-if="showBudget('rent')" class="budget-card ep-anchor">
                <span class="material-symbols-outlined budget-ico">home</span>
                <div>
                  <div class="budget-label">{{ $t('city.costs.rent') }}</div>
                  <div class="budget-val">R$ {{ formatMoney(city.budget.rent) }}</div>
                </div>
                <button v-if="editMode" type="button" class="ep-btn" @click="startEditBudget('rent')">{{ $t('city.admin.editBtn') }}</button>
              </div>
              <div v-if="showBudget('food')" class="budget-card ep-anchor">
                <span class="material-symbols-outlined budget-ico">restaurant</span>
                <div>
                  <div class="budget-label">{{ $t('city.costs.food') }}</div>
                  <div class="budget-val">R$ {{ formatMoney(city.budget.food) }}</div>
                </div>
                <button v-if="editMode" type="button" class="ep-btn" @click="startEditBudget('food')">{{ $t('city.admin.editBtn') }}</button>
              </div>
              <div v-if="showBudget('coworking')" class="budget-card ep-anchor">
                <span class="material-symbols-outlined budget-ico">laptop_mac</span>
                <div>
                  <div class="budget-label">{{ $t('city.costs.coworking') }}</div>
                  <div class="budget-val">R$ {{ formatMoney(city.budget.coworking) }}</div>
                </div>
                <button v-if="editMode" type="button" class="ep-btn" @click="startEditBudget('coworking')">{{ $t('city.admin.editBtn') }}</button>
              </div>
              <div v-if="showBudget('transport')" class="budget-card ep-anchor">
                <span class="material-symbols-outlined budget-ico">directions_bus</span>
                <div>
                  <div class="budget-label">{{ $t('city.costs.transport') }}</div>
                  <div class="budget-val">R$ {{ formatMoney(city.budget.transport) }}</div>
                </div>
                <button v-if="editMode" type="button" class="ep-btn" @click="startEditBudget('transport')">{{ $t('city.admin.editBtn') }}</button>
              </div>
            </div>
            <p v-if="editMode" class="hint-muted">{{ $t('city.costs.hintEmpty') }}</p>
          </section>

          <section id="insights" class="section-block scroll-target">
            <div class="insights-grid">
              <div>
                <h3 class="insight-heading high">
                  <span class="ico-circle ok"><span class="material-symbols-outlined">check_circle</span></span>
                  {{ $t('city.insights.highlightsTitle') }}
                </h3>
                <ul class="insight-list">
                  <li v-for="(h, hi) in city.highlights" :key="`h-${hi}`" class="insight-item ep-anchor">
                    <span class="insight-idx">{{ padIdx(hi) }}</span>
                    <p class="insight-body">
                      <strong v-if="h.title">{{ h.title }}.</strong>
                      {{ h.body }}
                    </p>
                    <button v-if="editMode" type="button" class="ep-btn" @click="startEditInsight('highlights', hi)">{{ $t('city.admin.editBtn') }}</button>
                  </li>
                </ul>
                <button v-if="editMode" type="button" class="btn-ghost btn-small" @click="addInsight('highlights')">{{ $t('city.insights.addHighlight') }}</button>
              </div>
              <div>
                <h3 class="insight-heading low">
                  <span class="ico-circle bad"><span class="material-symbols-outlined">cancel</span></span>
                  {{ $t('city.insights.challengesTitle') }}
                </h3>
                <ul class="insight-list">
                  <li v-for="(h, hi) in city.challenges" :key="`c-${hi}`" class="insight-item ep-anchor">
                    <span class="insight-idx bad">{{ padIdx(hi) }}</span>
                    <p class="insight-body">
                      <strong v-if="h.title">{{ h.title }}.</strong>
                      {{ h.body }}
                    </p>
                    <button v-if="editMode" type="button" class="ep-btn" @click="startEditInsight('challenges', hi)">{{ $t('city.admin.editBtn') }}</button>
                  </li>
                </ul>
                <button v-if="editMode" type="button" class="btn-ghost btn-small" @click="addInsight('challenges')">{{ $t('city.insights.addChallenge') }}</button>
              </div>
            </div>

            <div class="profile-cards">
              <div class="profile-card warn ep-anchor">
                <h4><span class="material-symbols-outlined">warning</span> {{ $t('city.profiles.notForTitle') }}</h4>
                <p v-for="(line, ni) in city.notFor" :key="`nf-${ni}`">{{ line }}</p>
                <p v-if="!city.notFor.length && !editMode" class="muted">—</p>
                <button v-if="editMode" type="button" class="ep-btn" @click="startEdit('notFor')">{{ $t('city.admin.editBtn') }}</button>
              </div>
              <div class="profile-card tip ep-anchor">
                <h4><span class="material-symbols-outlined">tips_and_updates</span> {{ $t('city.profiles.proTipTitle') }}</h4>
                <p>{{ city.proTip || "—" }}</p>
                <button v-if="editMode" type="button" class="ep-btn" @click="startEdit('proTip')">{{ $t('city.admin.editBtn') }}</button>
              </div>
            </div>
          </section>

          <section id="neighborhoods" class="section-block scroll-target">
            <h2 class="section-title">{{ $t('city.neighborhoods.title') }}</h2>
            <div class="nb-list">
              <div v-for="(nb, ni) in city.neighborhoods" :key="`nb-${ni}`" class="nb-card ep-anchor" @click="!editMode && null">
                <div class="nb-thumb">
                  <img v-if="nb.image" :src="nb.image" :alt="nb.name" />
                </div>
                <div class="nb-body">
                  <h4>{{ nb.name }}</h4>
                  <p>{{ nb.description }}</p>
                </div>
                <span class="material-symbols-outlined nb-arrow">chevron_right</span>
                <button v-if="editMode" type="button" class="ep-btn nb-edit" @click.stop="startEditNeighborhood(ni)">{{ $t('city.admin.editBtn') }}</button>
              </div>
            </div>
            <button v-if="editMode" type="button" class="btn-ghost btn-small" @click="addNeighborhood">{{ $t('city.neighborhoods.addNeighborhood') }}</button>
          </section>

          <section id="climate" class="section-block scroll-target climate-section ep-anchor">
            <div class="climate-head">
              <span class="material-symbols-outlined climate-ico">cloud_done</span>
              <div>
                <h2 class="section-title tight">{{ $t('city.climate.title') }}</h2>
                <p class="climate-sub">
                  {{ $t('city.climate.bestTimePrefix') }}
                  {{ city.climate.bestTimeLabel || "—" }}
                </p>
                <button v-if="editMode" type="button" class="ep-btn" @click="startEdit('climateLabel')">{{ $t('city.climate.editLabel') }}</button>
              </div>
            </div>
            <div class="climate-chart">
              <div v-for="(m, mi) in monthBars" :key="`m-${mi}`" class="climate-col">
                <div class="climate-bar-area">
                  <span
                    class="climate-temp"
                    :class="{ on: mi === highlightMonthIdx }"
                    :title="`Máx. típica ~${m.tempLabel}`"
                  >{{ m.tempLabel }}</span>
                  <div
                    class="climate-bar"
                    :class="{ on: mi === highlightMonthIdx }"
                    :style="{ height: m.heightPx + 'px' }"
                  />
                </div>
                <span class="climate-mon" :class="{ on: mi === highlightMonthIdx }">{{ m.label }}</span>
              </div>
            </div>
            <div v-if="editMode" class="climate-admin-actions">
              <button
                type="button"
                class="btn-climate-fetch"
                :disabled="climateFetchBusy"
                @click="fetchClimateAuto"
              >
                {{ climateFetchBusy ? $t('city.climate.fetchBusy') : $t('city.climate.fetchBtn') }}
              </button>
              <button type="button" class="ep-btn" @click="startEdit('climateMeta')">{{ $t('city.climate.editMeta') }}</button>
            </div>
            <p v-if="climateAutoNote" class="hint-muted climate-source-note">{{ climateAutoNote }}</p>
          </section>

          <section class="travel-row">
            <div class="travel-card ep-anchor">
              <span class="material-symbols-outlined travel-ico">flight_takeoff</span>
              <div>
                <div class="travel-label">{{ $t('city.travel.flightLabel', { from: city.travel.flightFrom || '—' }) }}</div>
                <div class="travel-val">R$ {{ formatMoney(city.travel.flightPrice) }}</div>
                <div class="travel-note">{{ city.travel.flightNote || $t('city.travel.flightNoteDefault') }}</div>
              </div>
              <button v-if="editMode" type="button" class="ep-btn" @click="startEdit('travelFlight')">{{ $t('city.admin.editBtn') }}</button>
            </div>
            <div class="travel-card ep-anchor">
              <span class="material-symbols-outlined travel-ico">schedule</span>
              <div>
                <div class="travel-label">{{ $t('city.travel.durationLabel') }}</div>
                <div class="travel-val">{{ city.travel.flightDuration || "—" }}</div>
                <div class="travel-note">{{ $t('city.travel.durationNote') }}</div>
              </div>
            </div>
            <div class="travel-card ep-anchor">
              <span class="material-symbols-outlined travel-ico">fact_check</span>
              <div>
                <div class="travel-label">{{ $t('city.travel.visaLabel') }}</div>
                <div class="travel-val">{{ city.travel.visaEase || "—" }}</div>
                <div class="travel-note">{{ city.travel.visaDuration || "—" }}</div>
              </div>
            </div>
            <div class="travel-card ep-anchor">
              <span class="material-symbols-outlined travel-ico">schedule_send</span>
              <div>
                <div class="travel-label">{{ $t('city.travel.timezoneLabel') }}</div>
                <div class="travel-val">{{ city.travel.timezoneOffset || "—" }}</div>
                <div class="travel-note">{{ city.travel.timezoneNote || $t('city.travel.timezoneNoteDefault') }} · {{ $t('city.travel.timezoneLocal') }} {{ localTimeHint }}</div>
              </div>
            </div>
          </section>

          <template v-if="!isKsamil">
            <CityEssentialAppsSection
              :apps="city.essentialApps"
              :edit-mode="editMode"
              @edit="startEditEssentialApp"
              @add="addEssentialApp"
            />

            <section v-if="showVisaSection" id="visa" class="section-block scroll-target city-visa-section">
              <div class="section-head city-visa-head">
                <h2 class="section-title tight">
                  <span class="material-symbols-outlined city-visa-ico">badge</span>
                  {{ $t('city.visa.title') }}
                </h2>
                <button v-if="editMode" type="button" class="ep-btn" @click="startEditVisaSection">{{ $t('city.visa.editBlock') }}</button>
              </div>
              <p class="city-visa-sub">{{ $t('city.visa.stepsLabel') }}</p>
              <ol class="city-visa-steps">
                <li v-for="(step, si) in visaStepsDisplay" :key="`vs-${si}`">{{ step }}</li>
              </ol>
              <p v-if="(city.visaSection?.visaCost || '').trim()" class="city-visa-meta">
                <span class="material-symbols-outlined city-visa-meta-ico">payments</span>
                {{ city.visaSection.visaCost }}
              </p>
              <p v-if="(city.visaSection?.visaDifficulty || '').trim()" class="city-visa-meta">
                <span class="material-symbols-outlined city-visa-meta-ico">trending_flat</span>
                {{ city.visaSection.visaDifficulty }}
              </p>
              <a
                v-if="visaCtaHref"
                class="btn-primary city-visa-cta"
                :href="visaCtaHref"
                target="_blank"
                rel="noopener noreferrer"
              >{{ $t('city.visa.applyBtn') }}</a>
              <p v-if="editMode && !visaStepsDisplay.length" class="hint-muted">{{ $t('city.visa.hintEmpty') }}</p>
            </section>

            <section v-if="showArrivalSection" id="arrival" class="section-block scroll-target city-arrival-section">
              <div class="section-head city-arrival-head">
                <h2 class="section-title tight">
                  <span class="material-symbols-outlined city-arrival-ico">flight_land</span>
                  {{ $t('city.arrival.title') }}
                </h2>
                <button v-if="editMode" type="button" class="ep-btn" @click="startEditArrival">{{ $t('city.arrival.editBlock') }}</button>
              </div>
              <div v-if="arrivalTipsDisplay.length" class="city-arrival-block">
                <h3 class="city-arrival-subtitle">{{ $t('city.arrival.tipsSubtitle') }}</h3>
                <ul class="city-arrival-list">
                  <li v-for="(t, ti) in arrivalTipsDisplay" :key="`at-${ti}`">{{ t }}</li>
                </ul>
              </div>
              <div v-if="arrivalDocsDisplay.length" class="city-arrival-block">
                <h3 class="city-arrival-subtitle">{{ $t('city.arrival.docsSubtitle') }}</h3>
                <ul class="city-arrival-list">
                  <li v-for="(d, di) in arrivalDocsDisplay" :key="`ad-${di}`">{{ d }}</li>
                </ul>
              </div>
              <p v-if="editMode && !arrivalTipsDisplay.length && !arrivalDocsDisplay.length" class="hint-muted">
                {{ $t('city.arrival.hintEmpty') }}
              </p>
            </section>

            <!-- Custom sections -->
            <template v-if="city.customSections && (city.customSections.length || editMode)">
              <!-- Edit mode: full TipTap editor -->
              <div v-if="editMode" class="section-block">
                <h2 class="section-title">Secções customizadas</h2>
                <CityCustomSectionsEditor v-model="city.customSections" />
              </div>
              <!-- View mode: render each section as HTML -->
              <template v-else>
                <section
                  v-for="section in city.customSections"
                  :key="section.anchorSlug || section.id"
                  :id="section.anchorSlug"
                  class="section-block scroll-target custom-section"
                >
                  <h2 class="section-title">{{ section.title }}</h2>
                  <!-- eslint-disable-next-line vue/no-v-html -->
                  <div class="custom-section-body" v-html="section.contentHtml"></div>
                </section>
              </template>
            </template>
          </template>

          <template v-else>
            <!-- Custom Ksamil sections -->
            <!-- #arrival (Como Chegar) -->
            <section id="arrival" class="section-block scroll-target ksamil-custom-section">
              <div class="section-head ksamil-section-header">
                <h2 class="section-title tight">
                  <span class="material-symbols-outlined ksamil-section-ico">route</span>
                  {{ currentLang === 'pt-BR' ? '1. Como Chegar' : '1. How to Get Here' }}
                </h2>
              </div>
              <p class="ksamil-section-subtitle">
                {{ currentLang === 'pt-BR' 
                  ? 'Guia visual de transporte de Tirana para Ksamil' 
                  : 'Visual transport directory from Tirana to Ksamil' }}
              </p>
              
              <p>
                {{ currentLang === 'pt-BR'
                  ? 'Ksamil está localizada no extremo sul da Albânia, a aproximadamente 270 km do Aeroporto de Tirana (TIA). Veja nosso fluxograma de viagem abaixo para ver como chegar até lá. Você pode reservar um transfer privado direto, pegar um ônibus de agência ou montar sua viagem passo a passo usando ônibus intermunicipais e táxis locais.'
                  : 'Ksamil is located in the beautiful southernmost pocket of Albania, approximately 270 km from Tirana Airport (TIA). Check out our visual travel flowchart below to see how to reach us. You can book an all-in-one direct private transfer, an agency coach, or easily build your own journey step-by-step using public buses and local taxis.' }}
              </p>
              
              <p class="ksamil-helper-text print-hide">
                <span class="material-symbols-outlined">swap_horiz</span>
                {{ currentLang === 'pt-BR'
                  ? 'Deslize horizontalmente no fluxograma abaixo para ver todas as opções! Clique em qualquer mapa para ampliar.'
                  : 'Swipe horizontally on the flowchart below to view all options side-by-side! Click any map thumbnail to enlarge.' }}
              </p>

              <!-- Flowchart Grid (Desktop horizontal scroll / Mobile collapsed) -->
              <div class="ksamil-flowchart-wrapper">
                <div class="ksamil-flowchart-grid">
                  <!-- Header row -->
                  <div class="flowchart-col-header" style="grid-column: 1; border-color: transparent;">
                    {{ currentLang === 'pt-BR' ? 'Etapas' : 'Stages' }}
                  </div>
                  <div class="flowchart-col-header" style="grid-column: 2;">
                    {{ currentLang === 'pt-BR' ? 'Mix 1 (Foco Táxi)' : 'Mix 1 (Taxi Focus)' }}
                  </div>
                  <div class="flowchart-col-header" style="grid-column: 3;">
                    {{ currentLang === 'pt-BR' ? 'Mix 2 (Foco Ônibus)' : 'Mix 2 (Bus Focus)' }}
                  </div>
                  <div class="flowchart-col-header" style="grid-column: 4;">
                    {{ currentLang === 'pt-BR' ? 'Ônibus Agência' : 'Agency Coach' }}
                  </div>
                  <div class="flowchart-col-header" style="grid-column: 5;">
                    {{ currentLang === 'pt-BR' ? 'Transfer / Carro' : 'Private Transfer / Car' }}
                  </div>

                  <!-- Axis timeline (Col 1) -->
                  <div class="axis-timeline-column">
                    <div class="axis-vertical-line"></div>
                    <div class="axis-node-point" style="top: 0%;">
                      <span class="axis-node-dot"></span>
                      <span class="axis-node-text">
                        {{ currentLang === 'pt-BR' ? 'Aeroporto TIA' : 'TIA Airport' }}
                        <small>Tirana</small>
                      </span>
                    </div>
                    <div class="axis-node-point" style="top: 33.3%;">
                      <span class="axis-node-dot"></span>
                      <span class="axis-node-text">
                        {{ currentLang === 'pt-BR' ? 'Terminal Tirana' : 'Tirana Bus' }}
                        <small>{{ currentLang === 'pt-BR' ? 'Ônibus' : 'Terminal' }}</small>
                      </span>
                    </div>
                    <div class="axis-node-point" style="top: 66.6%;">
                      <span class="axis-node-dot"></span>
                      <span class="axis-node-text">
                        Sarandë
                        <small>{{ currentLang === 'pt-BR' ? 'Parada Ônibus' : 'Bus Stop' }}</small>
                      </span>
                    </div>
                    <div class="axis-node-point" style="top: 100%;">
                      <span class="axis-node-dot"></span>
                      <span class="axis-node-text">
                        Ksamil
                        <small>{{ currentLang === 'pt-BR' ? 'Cidade' : 'Town' }}</small>
                      </span>
                    </div>
                  </div>

                  <!-- Row 2, Col 2: Taxi to Bus Terminal -->
                  <div class="flowchart-card" style="grid-row: 2; grid-column: 2;">
                    <span class="flowchart-card-stage">
                      {{ currentLang === 'pt-BR' ? 'Opções Mix — Etapa 1 (Aeroporto ➔ Terminal)' : 'Mix Options — Stage 1 (Airport ➔ Bus Station)' }}
                    </span>
                    <div>
                      <h5 class="flowchart-card-title">
                        <span class="material-symbols-outlined">local_taxi</span>
                        {{ currentLang === 'pt-BR' ? 'Táxi' : 'Taxi' }}
                      </h5>
                      <p class="flowchart-card-desc">
                        {{ currentLang === 'pt-BR'
                          ? 'Pegue um táxi direto do aeroporto até o terminal de ônibus intermunicipal. Extremamente prático para malas.'
                          : 'Take a direct taxi from the Airport straight to the intercity bus station. Highly convenient for luggage.' }}
                      </p>
                    </div>
                    <div class="choice-map-thumb" @click="openKsamilLightbox('/img/ksamil/Map - Tirana Airport to Tirana Bus Stop - By Car.jpg', currentLang === 'pt-BR' ? 'Rota do Aeroporto de Tirana ao Terminal de Ônibus' : 'Tirana Airport to Tirana Intercity Bus Terminal Route Map')">
                      <img src="/img/ksamil/Map - Tirana Airport to Tirana Bus Stop - By Car.jpg" alt="Airport to Bus Terminal Map" />
                      <div class="thumb-overlay">
                        <span class="material-symbols-outlined">zoom_in</span>
                        <span>{{ currentLang === 'pt-BR' ? 'Ver Mapa' : 'Zoom Map' }}</span>
                      </div>
                    </div>
                    <div class="flowchart-card-meta">
                      <span>{{ currentLang === 'pt-BR' ? '~ 20 Min' : '~ 20 Mins' }}</span>
                      <span class="flowchart-card-price">€20 - €30</span>
                    </div>
                  </div>

                  <!-- Row 2, Col 3: Shuttle Bus to Central Tirana -->
                  <div class="flowchart-card" style="grid-row: 2; grid-column: 3;">
                    <span class="flowchart-card-stage">
                      {{ currentLang === 'pt-BR' ? 'Opções Mix — Etapa 1 (Aeroporto ➔ Centro Tirana)' : 'Mix Options — Stage 1 (Airport ➔ Central Tirana)' }}
                    </span>
                    <div>
                      <h5 class="flowchart-card-title">
                        <span class="material-symbols-outlined">directions_bus</span>
                        {{ currentLang === 'pt-BR' ? 'Ônibus Shuttle' : 'Shuttle Bus' }}
                      </h5>
                      <p class="flowchart-card-desc">
                        {{ currentLang === 'pt-BR'
                          ? 'Pegue o ônibus Rinas Express a cada hora até o centro de Tirana. Requer táxi local para chegar ao terminal.'
                          : 'Take the hourly Rinas Express shuttle to central Tirana. Requires a local taxi/city bus to reach the terminal.' }}
                      </p>
                    </div>
                    <div class="flowchart-card-meta">
                      <span>{{ currentLang === 'pt-BR' ? '~ 35 Min' : '~ 35 Mins' }}</span>
                      <span class="flowchart-card-price">~ €4</span>
                    </div>
                  </div>

                  <!-- OR divider badge -->
                  <div class="or-divider-overlay" style="grid-row: 2; grid-column: 2 / span 2;">
                    <div class="or-divider-badge">{{ currentLang === 'pt-BR' ? 'ou' : 'or' }}</div>
                  </div>

                  <!-- Row 3, Col 2 & 3: Tirana to Sarandë Highway Bus -->
                  <div class="flowchart-card" style="grid-row: 3; grid-column: 2 / span 2;">
                    <span class="flowchart-card-stage">
                      {{ currentLang === 'pt-BR' ? 'Opções Mix — Etapa 2 (Tirana ➔ Sarandë)' : 'Mix Options — Stage 2 (Tirana ➔ Sarandë)' }}
                    </span>
                    <div>
                      <h5 class="flowchart-card-title">
                        <span class="material-symbols-outlined">directions_bus</span>
                        {{ currentLang === 'pt-BR' ? 'Ônibus Intermunicipal' : 'Intercity Bus' }}
                      </h5>
                      <p class="flowchart-card-desc">
                        {{ currentLang === 'pt-BR'
                          ? 'Embarque no ônibus intermunicipal regular que conecta o Terminal Sul e Oeste de Tirana a Sarandë.'
                          : 'Board the standard intercity coach running between Tirana South & West Bus Station and Sarandë.' }}
                      </p>
                    </div>
                    <div class="choice-map-thumb" @click="openKsamilLightbox('/img/ksamil/Map - TIrana to Sarande - By Bus.jpg', currentLang === 'pt-BR' ? 'Mapa do Ônibus Intermunicipal: Tirana a Sarandë' : 'Intercity Coach Map: Tirana to Sarandë')">
                      <img src="/img/ksamil/Map - TIrana to Sarande - By Bus.jpg" alt="Tirana to Sarandë Bus Map" />
                      <div class="thumb-overlay">
                        <span class="material-symbols-outlined">zoom_in</span>
                        <span>{{ currentLang === 'pt-BR' ? 'Ver Mapa' : 'Zoom Map' }}</span>
                      </div>
                    </div>
                    <button type="button" class="ksamil-btn-map-tip" @click="openKsamilLightbox('/img/ksamil/Map - Bus Stop Postion.jpg', currentLang === 'pt-BR' ? 'Localização da Parada de Ônibus em Tirana' : 'Tirana Bus Terminal Station Map — Tip on where to find the bus')">
                      <span class="material-symbols-outlined">map</span>
                      {{ currentLang === 'pt-BR' ? 'Ver Mapa do Terminal (Dica)' : 'View Station Map (Tip)' }}
                    </button>
                    <div class="flowchart-card-meta">
                      <span>{{ currentLang === 'pt-BR' ? '~ 5 Horas' : '~ 5 Hours' }}</span>
                      <span class="flowchart-card-price">€19</span>
                    </div>
                  </div>

                  <!-- Row 4, Col 2: Local Taxi Sarandë to Ksamil -->
                  <div class="flowchart-card" style="grid-row: 4; grid-column: 2;">
                    <span class="flowchart-card-stage">
                      {{ currentLang === 'pt-BR' ? 'Opções Mix — Etapa 3 (Sarandë ➔ Ksamil)' : 'Mix Options — Stage 3 (Sarandë ➔ Ksamil)' }}
                    </span>
                    <div>
                      <h5 class="flowchart-card-title">
                        <span class="material-symbols-outlined">local_taxi</span>
                        {{ currentLang === 'pt-BR' ? 'Táxi Local' : 'Local Taxi' }}
                      </h5>
                      <p class="flowchart-card-desc">
                        {{ currentLang === 'pt-BR'
                          ? 'Pegue um táxi direto no terminal em Sarandë para Ksamil. A forma mais rápida e confortável.'
                          : 'Grab a taxi at the Sarandë stop. Fast connection, dropping you directly in Ksamil.' }}
                      </p>
                    </div>
                    <div class="flowchart-card-meta">
                      <span>{{ currentLang === 'pt-BR' ? '~ 20 Min' : '~ 20 Mins' }}</span>
                      <span class="flowchart-card-price">€20 - €30</span>
                    </div>
                  </div>

                  <!-- Row 4, Col 3: Public Circular Bus Sarandë to Ksamil -->
                  <div class="flowchart-card" style="grid-row: 4; grid-column: 3;">
                    <span class="flowchart-card-stage">
                      {{ currentLang === 'pt-BR' ? 'Opções Mix — Etapa 3 (Sarandë ➔ Ksamil)' : 'Mix Options — Stage 3 (Sarandë ➔ Ksamil)' }}
                    </span>
                    <div>
                      <h5 class="flowchart-card-title">
                        <span class="material-symbols-outlined">directions_bus</span>
                        {{ currentLang === 'pt-BR' ? 'Ônibus Circular' : 'Circular Bus' }}
                      </h5>
                      <p class="flowchart-card-desc">
                        {{ currentLang === 'pt-BR'
                          ? 'Pegue o ônibus local de linha com destino a Butrint. Desça nas paradas do centro de Ksamil.'
                          : 'Take the local circular public bus heading for Butrint. Disembark at the Ksamil bus stops.' }}
                      </p>
                    </div>
                    <div class="choice-map-thumb" @click="openKsamilLightbox('/img/ksamil/Map - Sarande to Ksamil - By Bus.jpg', currentLang === 'pt-BR' ? 'Rota do Ônibus Local: Sarandë a Ksamil' : 'Local Bus Route Map: Sarandë to Ksamil')">
                      <img src="/img/ksamil/Map - Sarande to Ksamil - By Bus.jpg" alt="Sarandë to Ksamil local bus" />
                      <div class="thumb-overlay">
                        <span class="material-symbols-outlined">zoom_in</span>
                        <span>{{ currentLang === 'pt-BR' ? 'Ver Mapa' : 'Zoom Map' }}</span>
                      </div>
                    </div>
                    <!-- Details timetable -->
                    <details class="ksamil-timetable-details">
                      <summary>
                        <span>
                          <span class="material-symbols-outlined">schedule</span>
                          {{ currentLang === 'pt-BR' ? 'Horários do Ônibus' : 'Bus Timetable' }}
                        </span>
                        <span class="material-symbols-outlined dropdown-arrow">expand_more</span>
                      </summary>
                      <div class="ksamil-timetable-content">
                        <div class="ksamil-timetable-grid">
                          <div>
                            <div class="ksamil-timetable-header">{{ currentLang === 'pt-BR' ? 'De Sarandë' : 'From Sarandë' }}</div>
                            <div class="ksamil-timetable-times">
                              06:30<br>07:00<br>08:00<br>08:30<br>09:30<br>10:30<br>11:30<br>12:30<br>13:30<br>14:30<br>15:30<br>16:30<br>17:30
                            </div>
                          </div>
                          <div>
                            <div class="ksamil-timetable-header">{{ currentLang === 'pt-BR' ? 'De Butrint' : 'From Butrint' }}</div>
                            <div class="ksamil-timetable-times">
                              07:05<br>07:30<br>08:30<br>09:30<br>10:30<br>11:30<br>12:30<br>13:30<br>14:30<br>15:30<br>16:30<br>17:30<br>18:30
                            </div>
                          </div>
                        </div>
                      </div>
                    </details>
                    <div class="flowchart-card-meta">
                      <span>{{ currentLang === 'pt-BR' ? '~ 30 Min' : '~ 30 Mins' }}</span>
                      <span class="flowchart-card-price">200 Lekë / €2.50</span>
                    </div>
                  </div>

                  <!-- OR divider badge -->
                  <div class="or-divider-overlay" style="grid-row: 4; grid-column: 2 / span 2;">
                    <div class="or-divider-badge">{{ currentLang === 'pt-BR' ? 'ou' : 'or' }}</div>
                  </div>

                  <!-- Col 4: Agency Coach -->
                  <div class="flowchart-card span-vertical" style="grid-row: 2 / span 3; grid-column: 4; border-color: #D4A373;">
                    <span class="flowchart-card-stage">
                      {{ currentLang === 'pt-BR' ? 'Ônibus Direto (Tirana ➔ Ksamil Direto)' : 'Direct Coach (Tirana ➔ Ksamil Direct)' }}
                    </span>
                    <div>
                      <h5 class="flowchart-card-title" style="color: #c69260;">
                        <span class="material-symbols-outlined">directions_bus</span>
                        {{ currentLang === 'pt-BR' ? 'Ônibus de Agência' : 'Agency Coach' }}
                      </h5>
                      <p class="flowchart-card-desc">
                        {{ currentLang === 'pt-BR'
                          ? 'Um ônibus intermunicipal direto que viaja do terminal de Tirana diretamente para o centro de Ksamil, sem paradas em Sarandë.'
                          : 'A direct intercity coach line running from Tirana terminal straight down to the center of Ksamil, completely bypassing the need to change to taxis or local circular buses in Sarandë.' }}
                      </p>
                      <p class="flowchart-card-desc" style="font-size: 0.75rem; font-style: italic; color: #626d7a; margin-top: 8px;">
                        {{ currentLang === 'pt-BR'
                          ? '*Nota: Horários de partida e frequências variam, verifique direto com o provedor.*'
                          : '*Note: Departure schedules and frequency must be confirmed directly with the provider.*' }}
                      </p>
                      <div style="margin-top: 15px;">
                        <a href="https://wa.me/355675005999" target="_blank" rel="noopener noreferrer" class="ksamil-whatsapp-btn">
                          <span class="material-symbols-outlined">chat</span>
                          {{ currentLang === 'pt-BR' ? 'Contato Ksamil Explore' : 'Contact Ksamil Explore' }}
                        </a>
                      </div>
                    </div>
                    <div class="flowchart-card-meta">
                      <span>{{ currentLang === 'pt-BR' ? '~ 5,5 Horas' : '~ 5.5 Hours' }}</span>
                      <span class="flowchart-card-price">€30</span>
                    </div>
                  </div>

                  <!-- Col 5: Private Transfer / Car -->
                  <div class="flowchart-card span-vertical" style="grid-row: 2 / span 3; grid-column: 5; border-color: #4A9090;">
                    <span class="flowchart-card-stage">
                      {{ currentLang === 'pt-BR' ? 'Viagem Direta (Aeroporto Tirana ➔ Ksamil)' : 'Direct Travel (Tirana Airport ➔ Ksamil)' }}
                    </span>
                    <div>
                      <h5 class="flowchart-card-title" style="color: #4A9090;">
                        <span class="material-symbols-outlined">directions_car</span>
                        {{ currentLang === 'pt-BR' ? 'Transfer / Aluguel' : 'Transfer / Car' }}
                      </h5>
                      <p class="flowchart-card-desc" style="font-weight: 700; color: #4A9090; margin-bottom: 8px;">
                        {{ currentLang === 'pt-BR' ? 'Opções de Rota Direta' : 'Direct Travel Options' }}
                      </p>
                      <p class="flowchart-card-desc" style="margin-bottom: 4px; font-weight: 700;">
                        {{ currentLang === 'pt-BR' ? 'Opção A: Transfer Privado' : 'Option A: Private Transfer' }}
                      </p>
                      <p class="flowchart-card-desc" style="font-size: 0.75rem; line-height: 1.35; margin-bottom: 12px;">
                        {{ currentLang === 'pt-BR'
                          ? 'Requer reserva com antecedência. Um motorista aguarda você no desembarque de Tirana e leva direto a Ksamil.'
                          : 'Requires booking in advance by the guest. A driver meets you at Tirana Airport arrivals and drives you straight to Ksamil.' }}
                      </p>
                      <p class="flowchart-card-desc" style="margin-bottom: 4px; font-weight: 700;">
                        {{ currentLang === 'pt-BR' ? 'Opção B: Aluguel de Carro' : 'Option B: Car Rental' }}
                      </p>
                      <p class="flowchart-card-desc" style="font-size: 0.75rem; line-height: 1.35; margin-bottom: 12px;">
                        {{ currentLang === 'pt-BR'
                          ? 'Alugue um carro diretamente nos balcões do aeroporto de Tirana. Muito recomendado para explorar praias e vilas da Riviera Albanesa!'
                          : 'Rent a car directly at Tirana Airport rental desks. Highly recommended if you want full freedom to explore other beaches and cities during your holiday!' }}
                      </p>
                    </div>
                    <div class="flowchart-card-meta">
                      <span>{{ currentLang === 'pt-BR' ? '~ 4,5 Horas' : '~ 4.5 Hours' }}</span>
                      <span class="flowchart-card-price">€150+ / Var</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <!-- #logistics (Logística & Finanças) -->
            <section id="logistics" class="section-block scroll-target ksamil-custom-section">
              <div class="section-head ksamil-section-header">
                <h2 class="section-title tight">
                  <span class="material-symbols-outlined ksamil-section-ico">payments</span>
                  {{ currentLang === 'pt-BR' ? '2. Logística & Finanças' : '2. Logistics & Cash' }}
                </h2>
              </div>
              <p class="ksamil-section-subtitle">
                {{ currentLang === 'pt-BR' 
                  ? 'Dinheiro em espécie, guia de mercados locais e avisos de saúde' 
                  : 'Cash logistics, grocery guides, and essential health notices' }}
              </p>

              <!-- Cash vs Card -->
              <h3 class="ksamil-sub-title">
                <span class="material-symbols-outlined">payments</span>
                {{ currentLang === 'pt-BR' ? 'Dinheiro vs Cartão (Extremamente Importante!)' : 'Cash vs Card (Extremely Important!)' }}
              </h3>
              <p>
                {{ currentLang === 'pt-BR'
                  ? 'Embora os grandes supermercados e os estabelecimentos mais modernos aceitem cartões de crédito e débito internacionais, a Albânia e Ksamil ainda têm economias muito dominadas pelo dinheiro em espécie.'
                  : 'While major supermarkets, established hotels, and popular modern restaurants in Ksamil accept international credit and debit cards, Albania and Ksamil remain highly cash-dominated economies.' }}
              </p>

              <div class="ksamil-disclaimer-box secondary">
                <div class="disclaimer-header">
                  <span class="material-symbols-outlined">account_balance_wallet</span>
                  <span>{{ currentLang === 'pt-BR' ? 'Estratégia Prática de Dinheiro' : 'Practical Cash Strategy' }}</span>
                </div>
                <p class="disclaimer-body">
                  {{ currentLang === 'pt-BR'
                    ? 'Sempre carregue Euros ou Leks (moeda local da Albânia) em espécie. O dinheiro físico é necessário para alugar espreguiçadeiras na praia, pagar o ônibus circular local, barracas de frutas e tabernas menores. Caixas eletrônicos (ATMs) estão disponíveis na cidade, mas costumam cobrar taxas de saque elevadas.'
                    : 'Always carry Euros or Leks (local Albanian currency) in cash. Cash is required for beach sunbed rentals, small local circular buses, beachside fruit stands, convenience stores, and smaller seafood taverns. Local ATMs are available in town, but they frequently charge high transaction withdrawal fees.' }}
                </p>
              </div>

              <!-- Grocery stores -->
              <h3 class="ksamil-sub-title">
                <span class="material-symbols-outlined">shopping_basket</span>
                {{ currentLang === 'pt-BR' ? 'Compras & Supermercados Locais' : 'Local Groceries & Supermarkets' }}
              </h3>
              <p>
                {{ currentLang === 'pt-BR'
                  ? 'Recomendamos dois principais mercados locais em Ksamil para abastecer sua despensa com café da manhã, frutas frescas e bebidas:'
                  : 'We recommend two main grocery stores in Ksamil to stock up on breakfasts, fresh fruits, and drinks:' }}
              </p>

              <div class="ksamil-features-grid">
                <div class="ksamil-feature-card">
                  <h4 class="feature-card-title">
                    <span class="material-symbols-outlined">shopping_cart</span>
                    Big Market
                  </h4>
                  <p>
                    {{ currentLang === 'pt-BR'
                      ? 'Ideal para compras completas de mercado, oferecendo uma variedade maior de queijos frescos, frios, pães, vinhos e marcas internacionais.'
                      : 'Great for full shopping trips, offering a wider selection of fresh cheeses, cold cuts, breads, wines, and international brands.' }}
                  </p>
                </div>
                <div class="ksamil-feature-card">
                  <h4 class="feature-card-title">
                    <span class="material-symbols-outlined">store</span>
                    Small Market / Mercadinhos
                  </h4>
                  <p>
                    {{ currentLang === 'pt-BR'
                      ? 'Perfeito para reposições rápidas do dia a dia, bebidas geladas, galões de água mineral, lanches e itens simples de higiene.'
                      : 'Perfect for quick daily support items, cold drinks, water gallons, snacks, or simple hygiene replenishments.' }}
                  </p>
                </div>
              </div>

              <!-- Water Advice -->
              <div class="ksamil-disclaimer-box warning">
                <div class="disclaimer-header">
                  <span class="material-symbols-outlined">water_drop</span>
                  <span>{{ currentLang === 'pt-BR' ? '⚠️ Água da Torneira: Alerta de Saúde' : '⚠️ Tap Water Health Notice' }}</span>
                </div>
                <p class="disclaimer-body">
                  {{ currentLang === 'pt-BR'
                    ? 'A água da torneira em Ksamil e em toda a região costeira da Albânia NÃO é recomendada para consumo. Ela é perfeitamente segura para tomar banho, lavar roupas e louças. Para beber, fazer café ou cozinhar, compre galões de água mineral nos supermercados locais assim que chegar.'
                    : 'The tap water in Ksamil and the coastal Albanian region is NOT recommended for drinking. It is perfectly safe for showers, laundry, and washing dishes. For drinking, coffee, and cooking, please buy mineral water gallons from the local supermarkets immediately upon arrival.' }}
                </p>
              </div>

              <!-- Limescale effect -->
              <h3 class="ksamil-sub-title">
                <span class="material-symbols-outlined">soap</span>
                {{ currentLang === 'pt-BR' ? 'O "Efeito Calcário" (Pele & Cabelo)' : 'The "Limescale Effect" (Skin & Hair Care)' }}
              </h3>
              <p>
                {{ currentLang === 'pt-BR'
                  ? 'A água encanada da região possui uma alta concentração natural de carbonato de cálcio (calcário ou limescale).'
                  : 'The local groundwater in the region features a naturally high concentration of calcium carbonate (limescale).' }}
              </p>
              <div class="ksamil-info-card-box">
                <div class="info-card-header">
                  <span class="material-symbols-outlined">info</span>
                  <span>{{ currentLang === 'pt-BR' ? 'O que esperar e dicas de cuidados:' : 'What to expect and skincare tips:' }}</span>
                </div>
                <p class="info-card-body">
                  {{ currentLang === 'pt-BR'
                    ? 'É completamente normal notar a pele ligeiramente seca e os cabelos mais ásperos ao longo dos primeiros dias após os banhos. Não se preocupe — é uma reação temporária aos minerais da água local! Recomendamos usar hidratantes corporais e um bom condicionador ou máscara capilar para manter a hidratação das férias.'
                    : 'It is completely normal to notice your skin feeling slightly dry and your hair feeling coarser or less soft after showering during the first few days. Don\'t worry—this is just a temporary reaction to the rich local minerals! We highly recommend packed moisturizing skin lotions and a deep hair conditioner or mask to keep your skin and hair hydrated during your beach holiday.' }}
                </p>
              </div>
            </section>

            <!-- #mobility (Mobilidade) -->
            <section id="mobility" class="section-block scroll-target ksamil-custom-section">
              <div class="section-head ksamil-section-header">
                <h2 class="section-title tight">
                  <span class="material-symbols-outlined ksamil-section-ico">directions_car</span>
                  {{ currentLang === 'pt-BR' ? '3. Mobilidade' : '3. Mobility' }}
                </h2>
              </div>
              <p class="ksamil-section-subtitle">
                {{ currentLang === 'pt-BR' 
                  ? 'Segurança nas ruas, topografia, aluguel de scooters e conexões locais' 
                  : 'Local walking safety, steep terrain warnings, scooter rentals, and regional tours' }}
              </p>

              <!-- Safety & Topography -->
              <h3 class="ksamil-sub-title">
                <span class="material-symbols-outlined">hiking</span>
                {{ currentLang === 'pt-BR' ? 'Segurança & Ladeiras (A Topografia de Ksamil)' : 'Safety & Hills (The Ksamil Topography)' }}
              </h3>
              <p>
                {{ currentLang === 'pt-BR'
                  ? 'Ksamil é uma cidade muito tranquila e segura. É extremamente seguro caminhar a pé a qualquer hora do dia ou da noite. Os moradores locais são amigáveis e a vibe da praia é relaxante.'
                  : 'Ksamil is a highly peaceful, welcoming town. It is extremely safe to walk around on foot at any time of the day or night. Local residents are warm, and the coastal atmosphere is exceptionally relaxing.' }}
              </p>

              <div class="ksamil-disclaimer-box turquoise">
                <div class="disclaimer-header">
                  <span class="material-symbols-outlined">terrain</span>
                  <span>{{ currentLang === 'pt-BR' ? 'Alerta de Ladeiras e Ruas Íngremes' : 'Steep Terrain Warning' }}</span>
                </div>
                <p class="disclaimer-body">
                  {{ currentLang === 'pt-BR'
                    ? 'Ksamil possui uma geografia acidentada. Muitas ruas e ladeiras residenciais na cidade têm subidas íngremes. Recomenda-se calçar tênis confortáveis ou sandálias firmes para caminhar com maior facilidade pelas ruas secundárias.'
                    : 'Ksamil has hilly topography. Many streets and residential paths feature steep slopes. Wearing comfortable, supportive walking shoes or solid sandals is highly recommended to easily navigate the neighborhood.' }}
                </p>
              </div>

              <!-- Scooter rental -->
              <h3 class="ksamil-sub-title">
                <span class="material-symbols-outlined">two_wheeler</span>
                {{ currentLang === 'pt-BR' ? 'Aluguel de Scooters (Motonetas)' : 'Scooter Rentals' }}
              </h3>
              <p>
                {{ currentLang === 'pt-BR'
                  ? 'Se você quiser evitar caminhadas nas ladeiras sob o sol forte de verão ou quiser explorar enseadas paradisíacas e escondidas na Riviera Albanesa, alugar uma scooter é uma escolha perfeita.'
                  : 'If you want to bypass walking uphill under the summer sun, or wish to explore distant hidden coves along the Albanian Riviera, renting a scooter is the perfect choice.' }}
              </p>
              <p>
                {{ currentLang === 'pt-BR'
                  ? 'O aluguel de motonetas é super popular e fácil na cidade. As tarifas médias ficam entre €10 e €20 por dia. Existem várias agências que realizam o aluguel no centro de Ksamil.'
                  : 'Scooter hire is very popular and straightforward in Ksamil. Average rates hover around €10 to €20 per day. There are several reliable local agencies in town.' }}
              </p>

              <!-- Local Attractions via Bus -->
              <h3 class="ksamil-sub-title">
                <span class="material-symbols-outlined">directions_bus</span>
                {{ currentLang === 'pt-BR' ? 'Explorando com o Ônibus Circular Local' : 'Exploring via Public Circular Bus' }}
              </h3>
              <p>
                {{ currentLang === 'pt-BR'
                  ? 'O ônibus de linha local é econômico, confiável e ideal para passeios rápidos de um dia na região. Você pode usá-lo para visitar duas grandes atrações próximas:'
                  : 'The local public circular bus is cheap, reliable, and convenient for day trips. You can take it from the stops on the main road to visit two major nearby attractions:' }}
              </p>

              <div class="ksamil-features-grid">
                <div class="ksamil-feature-card">
                  <h4 class="feature-card-title">
                    <span class="material-symbols-outlined">location_city</span>
                    {{ currentLang === 'pt-BR' ? 'Cidade de Sarandë' : 'City of Sarandë' }}
                  </h4>
                  <p>
                    {{ currentLang === 'pt-BR'
                      ? 'Excelente para passeios noturnos no calçadão principal, compras de souvenirs, restaurantes com vista para o mar e embarque para Corfu (Grécia).'
                      : 'Great for seaside dining, late-evening walks along the main pedestrian boulevard, shopping, or boarding ferries to Greece.' }}
                  </p>
                </div>
                <div class="ksamil-feature-card">
                  <h4 class="feature-card-title">
                    <span class="material-symbols-outlined">theater_comedy</span>
                    {{ currentLang === 'pt-BR' ? 'Parque Nacional de Butrint' : 'Butrint National Park' }}
                  </h4>
                  <p>
                    {{ currentLang === 'pt-BR'
                      ? 'Apenas alguns minutos ao sul de Ksamil! Explore as impressionantes ruínas arqueológicas gregas, romanas e bizantinas em meio à floresta e lagoas (Patrimônio Mundial da UNESCO).'
                      : 'Located just minutes down the road! Explore ancient Greek, Roman, and Byzantine ruins nestled in a beautiful nature reserve (UNESCO World Heritage Site).' }}
                  </p>
                </div>
              </div>
            </section>

            <!-- #leisure (Praias & Lazer) -->
            <section id="leisure" class="section-block scroll-target ksamil-custom-section">
              <div class="section-head ksamil-section-header">
                <h2 class="section-title tight">
                  <span class="material-symbols-outlined ksamil-section-ico">umbrella</span>
                  {{ currentLang === 'pt-BR' ? '4. Praias & Lazer' : '4. Beaches & Leisure' }}
                </h2>
              </div>
              <p class="ksamil-section-subtitle">
                {{ currentLang === 'pt-BR' 
                  ? 'As enseadas de águas azul-turquesa cristalinas em Ksamil' 
                  : 'Crystal-clear turquoise waters in Ksamil' }}
              </p>

              <!-- Walking beaches -->
              <h3 class="ksamil-sub-title">
                <span class="material-symbols-outlined">beach_access</span>
                {{ currentLang === 'pt-BR' ? 'Praias Lindas para Ir a Pé' : 'Stunning Walking-Distance Beaches' }}
              </h3>
              <p>
                {{ currentLang === 'pt-BR'
                  ? 'Ksamil é amplamente reconhecida por suas areias brancas finas de estilo caribenho e mar calmo. Algumas praias incríveis para curtir:'
                  : 'Ksamil is widely celebrated for its clean, Caribbean-like white sands and calm waters. You can walk and relax at several stunning spots in town:' }}
              </p>

              <div class="ksamil-features-grid">
                <div class="ksamil-feature-card">
                  <h4 class="feature-card-title">
                    <span class="material-symbols-outlined">waves</span>
                    Augustus Beach
                  </h4>
                  <p>
                    {{ currentLang === 'pt-BR'
                      ? 'Uma enseada charmosa e mais reservada, ideal para banhos matinais calmos.'
                      : 'A beautiful, peaceful cove perfect for early morning swimming and relaxing.' }}
                  </p>
                </div>
                <div class="ksamil-feature-card">
                  <h4 class="feature-card-title">
                    <span class="material-symbols-outlined">umbrella</span>
                    Beach 7
                  </h4>
                  <p>
                    {{ currentLang === 'pt-BR'
                      ? 'Uma faixa cênica e charmosa de areia branca, perfeita para alugar espreguiçadeiras e passar o dia relaxando no mar.'
                      : 'A highly scenic local stretch of sand, ideal for booking a comfortable lounger and spending a full, lazy sunbathing day.' }}
                  </p>
                </div>
              </div>

              <!-- Aerial Drone shot -->
              <div class="ksamil-map-container">
                <div class="ksamil-map-image-wrapper" @click="openKsamilLightbox('/img/ksamil/Augustos Beach Drone Picture.JPG', currentLang === 'pt-BR' ? 'Foto aérea de Augustus Beach em Ksamil' : 'Aerial Drone Shot of Augustus Beach, Ksamil')">
                  <img src="/img/ksamil/Augustos Beach Drone Picture.JPG" alt="Drone view of Augustus Beach" />
                  <div class="ksamil-map-overlay-trigger">
                    <span class="material-symbols-outlined">zoom_in</span>
                    <span>{{ currentLang === 'pt-BR' ? 'Ver Foto Aérea' : 'Zoom Photo' }}</span>
                  </div>
                </div>
                <div class="ksamil-map-caption">
                  <span class="material-symbols-outlined">photo_camera</span>
                  {{ currentLang === 'pt-BR'
                    ? 'As incríveis águas cristalinas de Augustus Beach, Ksamil.'
                    : 'Stunning crystalline turquoise waters of Augustus Beach, Ksamil.' }}
                </div>
              </div>

              <p style="margin-top: 25px;">
                <strong>{{ currentLang === 'pt-BR' ? 'Outras Praias Famosas:' : 'Other Popular Beaches:' }}</strong>
                {{ currentLang === 'pt-BR'
                  ? ' Não deixe de explorar praias movimentadas e lindas como a Bora Bora Beach, Ksamil Beach, Lori Beach e Paradise Beach. Elas oferecem bares, restaurantes à beira-mar e boa estrutura de espreguiçadeiras e guarda-sóis.'
                  : ' Don\'t miss visiting famous coastal spots like Bora Bora Beach, Ksamil Beach, Lori Beach, and Paradise Beach. They all offer beachside restaurants, cocktail bars, and comfortable sunbed rentals.' }}
              </p>

              <!-- Three Islands Kayak golden tip -->
              <div class="ksamil-info-card-box highlight">
                <div class="info-card-header">
                  <span class="material-symbols-outlined">star</span>
                  <span>{{ currentLang === 'pt-BR' ? 'Dica de Ouro: Passeio de Caiaque nas Três Ilhas' : 'Golden Tip: The Ksamil Three Islands Kayaking' }}</span>
                </div>
                <p class="info-card-body">
                  {{ currentLang === 'pt-BR'
                    ? 'As Três Ilhas de Ksamil são pequenas ilhas verdes desabitadas flutuando logo à frente da costa, formando uma paisagem maravilhosa!'
                    : 'The Three Islands of Ksamil are beautiful, uninhabited green islands floating just off our main coastline, creating a truly breathtaking panorama!' }}
                  <br><br>
                  <strong>{{ currentLang === 'pt-BR' ? 'A Experiência que Recomendamos:' : 'The Experience We Recommend:' }}</strong>
                  {{ currentLang === 'pt-BR'
                    ? 'Vá até uma das praias centrais e alugue um caiaque ou prancha de Stand-Up Paddle (SUP). A distância para remar é bem curta e segura, mesmo para quem nunca remou. Você pode atracar nas ilhas e curtir pequenas enseadas secretas de cascalho acessíveis apenas pelo mar. É uma das aventuras de lazer mais fantásticas de toda a Albânia!'
                    : 'Head down to the main beach and rent a kayak or Stand-Up Paddleboard (SUP). The rowing distance is very short and safe, even for beginners. You can row out to the islands, pull your kayak onto the shore, and explore tiny, secluded pebble beaches that are completely inaccessible by land. It is easily one of the most rewarding and memorable outdoor activities in all of Albania!' }}
                </p>
              </div>
            </section>
          </template>

        </div>
      </div>

      <footer class="city-footer">
        <div class="city-container city-footer-inner">
          <span class="city-logo muted-logo">{{ $t('city.footer.brand') }}</span>
          <div class="footer-links">
            <router-link to="/discover">{{ $t('city.footer.destinations') }}</router-link>
            <span class="footer-copy">{{ $t('city.footer.copyright', { year: new Date().getFullYear() }) }}</span>
          </div>
        </div>
      </footer>
    </template>

    <Teleport to="body">
      <div
        v-if="editMode && activeHeroIdx !== null"
        class="hero-url-modal-backdrop"
        role="presentation"
        @click.self="cancelHeroEdit"
      >
        <div
          class="hero-url-modal city-modal-flex"
          role="dialog"
          aria-modal="true"
          aria-labelledby="hero-url-modal-title"
          @click.stop
        >
          <div class="city-modal-body">
            <h2 id="hero-url-modal-title" class="hero-url-modal-title">{{ $t('city.admin.heroUrlTitle') }}</h2>
            <p class="hero-url-modal-sub">
              {{ $t('city.admin.heroUrlSub', { pos: (activeHeroIdx ?? 0) + 1 }) }}
            </p>
            <label class="hero-url-modal-label" for="hero-url-modal-input">{{ $t('city.admin.heroUrlLabel') }}</label>
            <input
              id="hero-url-modal-input"
              ref="heroUrlModalInput"
              v-model="heroDraft"
              type="url"
              class="ep-input ep-input-full hero-url-modal-input"
              placeholder="https://…"
              @keydown.enter.prevent="saveHeroModal"
            />

            <div class="hero-upload-container" style="margin-top: 15px;">
              <span class="hero-url-modal-label">{{ currentLang === 'pt-BR' ? 'Ou envie do computador:' : 'Or upload from computer:' }}</span>
              <label class="hero-upload-label" style="display: flex; align-items: center; justify-content: center; gap: 8px; border: 1.5px dashed var(--outline); padding: 12px; border-radius: 8px; cursor: pointer; background: var(--surface-low); color: var(--on-surface); font-size: 0.85rem; font-weight: 600; margin-top: 6px; transition: all 0.2s;">
                <span class="material-symbols-outlined" style="font-size: 1.25rem;">upload</span>
                <span>{{ heroUploadBusy ? (currentLang === 'pt-BR' ? 'Enviando...' : 'Uploading...') : (currentLang === 'pt-BR' ? 'Escolher arquivo' : 'Choose file') }}</span>
                <input
                  type="file"
                  accept="image/*"
                  style="display: none;"
                  :disabled="heroUploadBusy"
                  @change="handleHeroUpload"
                />
              </label>
            </div>
          </div>
          <div class="hero-url-modal-actions city-modal-footer">
            <button type="button" class="btn-ghost" :disabled="saving || heroUploadBusy" @click="cancelHeroEdit">{{ $t('city.admin.cancel') }}</button>
            <button type="button" class="btn-primary city-modal-save-btn" :disabled="saving || heroUploadBusy" @click="saveHeroModal">
              {{ saving ? $t('city.admin.saving') : $t('city.admin.save') }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <div
        v-if="editMode && showAdminEditorModal"
        class="hero-url-modal-backdrop"
        role="presentation"
        @click.self="dismissAdminEditor"
      >
        <div
          class="hero-url-modal city-modal-flex"
          :class="{
            'city-edit-modal--wide':
              activeField === 'travelFlight' ||
              activeField === 'climateMeta' ||
              activeField === 'visaSection' ||
              activeField === 'arrival',
          }"
          role="dialog"
          aria-modal="true"
          @click.stop
        >
          <template v-if="insightEdit">
            <div class="city-modal-body">
              <h2 class="hero-url-modal-title">{{ insightEdit.list === "highlights" ? $t('city.admin.insightHighlightTitle') : $t('city.admin.insightChallengeTitle') }}</h2>
              <p class="hero-url-modal-sub">{{ $t('city.admin.insightSubLabel', { n: (insightEdit.idx ?? 0) + 1 }) }}</p>
              <div class="city-edit-modal-stack">
                <label class="ep-label" for="adm-ins-title">{{ $t('city.admin.insightTitleLabel') }}</label>
                <input id="adm-ins-title" ref="adminModalPrimaryInput" v-model="insightDraft.title" class="ep-input ep-input-full" />
                <label class="ep-label" for="adm-ins-body">{{ $t('city.admin.insightBodyLabel') }}</label>
                <textarea id="adm-ins-body" v-model="insightDraft.body" class="ep-textarea" rows="4" />
              </div>
            </div>
            <div class="city-edit-modal-actions city-modal-footer">
              <button type="button" class="btn-ghost" :disabled="saving" @click="dismissAdminEditor">{{ $t('city.admin.cancel') }}</button>
              <button type="button" class="btn-ghost" :disabled="saving" @click="removeCurrentInsight">{{ $t('city.admin.remove') }}</button>
              <button type="button" class="btn-primary city-modal-save-btn" :disabled="saving" @click="saveAdminModal">
                {{ saving ? $t('city.admin.saving') : $t('city.admin.save') }}
              </button>
            </div>
          </template>

          <template v-else-if="budgetEditKey">
            <div class="city-modal-body">
              <h2 class="hero-url-modal-title">{{ $t('city.admin.budgetTitle', { label: budgetEditLabel }) }}</h2>
              <p class="hero-url-modal-sub">{{ $t('city.admin.budgetSub') }}</p>
              <input
                ref="adminModalPrimaryInput"
                v-model.number="budgetDraft"
                type="number"
                class="ep-input ep-input-full"
                :placeholder="$t('city.admin.budgetPlaceholder')"
              />
            </div>
            <div class="city-edit-modal-actions city-modal-footer">
              <button type="button" class="btn-ghost" :disabled="saving" @click="dismissAdminEditor">{{ $t('city.admin.cancel') }}</button>
              <button type="button" class="btn-primary city-modal-save-btn" :disabled="saving" @click="saveAdminModal">
                {{ saving ? $t('city.admin.saving') : $t('city.admin.save') }}
              </button>
            </div>
          </template>

          <template v-else-if="activeField === 'country'">
            <div class="city-modal-body">
              <h2 class="hero-url-modal-title">{{ $t('city.admin.countryTitle') }}</h2>
              <input ref="adminModalPrimaryInput" v-model="city.country" class="ep-input ep-input-full" />
            </div>
            <div class="city-edit-modal-actions city-modal-footer">
              <button type="button" class="btn-ghost" :disabled="saving" @click="dismissAdminEditor">{{ $t('city.admin.cancel') }}</button>
              <button type="button" class="btn-primary city-modal-save-btn" :disabled="saving" @click="saveAdminModal">
                {{ saving ? $t('city.admin.saving') : $t('city.admin.save') }}
              </button>
            </div>
          </template>

          <template v-else-if="activeField === 'name'">
            <div class="city-modal-body">
              <h2 class="hero-url-modal-title">{{ $t('city.admin.nameTitle') }}</h2>
              <input ref="adminModalPrimaryInput" v-model="city.name" class="ep-input ep-input-full" />
            </div>
            <div class="city-edit-modal-actions city-modal-footer">
              <button type="button" class="btn-ghost" :disabled="saving" @click="dismissAdminEditor">{{ $t('city.admin.cancel') }}</button>
              <button type="button" class="btn-primary city-modal-save-btn" :disabled="saving" @click="saveAdminModal">
                {{ saving ? $t('city.admin.saving') : $t('city.admin.save') }}
              </button>
            </div>
          </template>

          <template v-else-if="activeField === 'tags'">
            <div class="city-modal-body">
              <h2 class="hero-url-modal-title">{{ $t('city.admin.tagsTitle') }}</h2>
              <p class="hero-url-modal-sub">{{ $t('city.admin.tagsSub') }}</p>
              <textarea ref="adminModalPrimaryInput" v-model="tagsDraft" class="ep-textarea" rows="3" />
            </div>
            <div class="city-edit-modal-actions city-modal-footer">
              <button type="button" class="btn-ghost" :disabled="saving" @click="dismissAdminEditor">{{ $t('city.admin.cancel') }}</button>
              <button type="button" class="btn-primary city-modal-save-btn" :disabled="saving" @click="saveAdminModal">
                {{ saving ? $t('city.admin.saving') : $t('city.admin.save') }}
              </button>
            </div>
          </template>

          <template v-else-if="activeField === 'heroVibeLine'">
            <div class="city-modal-body">
              <h2 class="hero-url-modal-title">{{ $t('city.admin.vibeTitle') }}</h2>
              <p class="hero-url-modal-sub">{{ $t('city.admin.vibeSub') }}</p>
              <input ref="adminModalPrimaryInput" v-model="city.heroVibeLine" class="ep-input ep-input-full" />
            </div>
            <div class="city-edit-modal-actions city-modal-footer">
              <button type="button" class="btn-ghost" :disabled="saving" @click="dismissAdminEditor">{{ $t('city.admin.cancel') }}</button>
              <button type="button" class="btn-primary city-modal-save-btn" :disabled="saving" @click="saveAdminModal">
                {{ saving ? $t('city.admin.saving') : $t('city.admin.save') }}
              </button>
            </div>
          </template>

          <template v-else-if="activeField === 'vital'">
            <div class="city-modal-body">
              <h2 class="hero-url-modal-title">{{ $t('city.admin.vitalTitle') }}</h2>
              <p class="hero-url-modal-sub">{{ $t('city.admin.vitalSubLabel', { n: (vitalIdx ?? 0) + 1 }) }}</p>
              <div class="city-edit-modal-stack">
                <label class="ep-label">{{ $t('city.admin.vitalLabelField') }}</label>
                <input ref="adminModalPrimaryInput" v-model="vitalDraft.label" class="ep-input ep-input-full" />
                <label class="ep-label">{{ $t('city.admin.vitalValueField') }}</label>
                <input v-model.number="vitalDraft.value" type="number" step="0.1" class="ep-input ep-input-full" />
                <label class="ep-label">{{ $t('city.admin.vitalMaxField') }}</label>
                <input v-model.number="vitalDraft.max" type="number" step="0.1" class="ep-input ep-input-full" />
                <label class="ep-label">{{ $t('city.admin.vitalDisplayField') }}</label>
                <input v-model="vitalDraft.display" class="ep-input ep-input-full" />
              </div>
            </div>
            <div class="city-edit-modal-actions city-modal-footer">
              <button type="button" class="btn-ghost" :disabled="saving" @click="dismissAdminEditor">{{ $t('city.admin.cancel') }}</button>
              <button type="button" class="btn-primary city-modal-save-btn" :disabled="saving" @click="saveAdminModal">
                {{ saving ? $t('city.admin.saving') : $t('city.admin.save') }}
              </button>
            </div>
          </template>

          <template v-else-if="activeField === 'notFor'">
            <div class="city-modal-body">
              <h2 class="hero-url-modal-title">{{ $t('city.admin.notForTitle') }}</h2>
              <p class="hero-url-modal-sub">{{ $t('city.admin.notForSub') }}</p>
              <textarea ref="adminModalPrimaryInput" v-model="notForDraft" class="ep-textarea" rows="6" />
            </div>
            <div class="city-edit-modal-actions city-modal-footer">
              <button type="button" class="btn-ghost" :disabled="saving" @click="dismissAdminEditor">{{ $t('city.admin.cancel') }}</button>
              <button type="button" class="btn-primary city-modal-save-btn" :disabled="saving" @click="saveAdminModal">
                {{ saving ? $t('city.admin.saving') : $t('city.admin.save') }}
              </button>
            </div>
          </template>

          <template v-else-if="activeField === 'proTip'">
            <div class="city-modal-body">
              <h2 class="hero-url-modal-title">{{ $t('city.admin.proTipTitle') }}</h2>
              <textarea ref="adminModalPrimaryInput" v-model="city.proTip" class="ep-textarea" rows="5" />
            </div>
            <div class="city-edit-modal-actions city-modal-footer">
              <button type="button" class="btn-ghost" :disabled="saving" @click="dismissAdminEditor">{{ $t('city.admin.cancel') }}</button>
              <button type="button" class="btn-primary city-modal-save-btn" :disabled="saving" @click="saveAdminModal">
                {{ saving ? $t('city.admin.saving') : $t('city.admin.save') }}
              </button>
            </div>
          </template>

          <template v-else-if="activeField === 'neighborhood'">
            <div class="city-modal-body">
              <h2 class="hero-url-modal-title">{{ $t('city.admin.neighborhoodTitle') }}</h2>
              <p class="hero-url-modal-sub">{{ $t('city.admin.neighborhoodSubLabel', { n: (nbIdx ?? 0) + 1 }) }}</p>
              <div class="city-edit-modal-stack">
                <label class="ep-label">{{ $t('city.admin.neighborhoodNameField') }}</label>
                <input ref="adminModalPrimaryInput" v-model="nbDraft.name" class="ep-input ep-input-full" />
                <label class="ep-label">{{ $t('city.admin.neighborhoodDescField') }}</label>
                <textarea v-model="nbDraft.description" class="ep-textarea" rows="3" />
                <label class="ep-label">{{ $t('city.admin.neighborhoodImageField') }}</label>
                <input v-model="nbDraft.image" type="url" class="ep-input ep-input-full" placeholder="https://…" />
              </div>
            </div>
            <div class="city-edit-modal-actions city-modal-footer">
              <button type="button" class="btn-ghost" :disabled="saving" @click="dismissAdminEditor">{{ $t('city.admin.cancel') }}</button>
              <button type="button" class="btn-ghost" :disabled="saving" @click="removeCurrentNeighborhood">{{ $t('city.admin.removeNeighborhood') }}</button>
              <button type="button" class="btn-primary city-modal-save-btn" :disabled="saving" @click="saveAdminModal">
                {{ saving ? $t('city.admin.saving') : $t('city.admin.save') }}
              </button>
            </div>
          </template>

          <template v-else-if="activeField === 'climateLabel'">
            <div class="city-modal-body">
              <h2 class="hero-url-modal-title">{{ $t('city.admin.climateTitle') }}</h2>
              <input ref="adminModalPrimaryInput" v-model="city.climate.bestTimeLabel" class="ep-input ep-input-full" />
            </div>
            <div class="city-edit-modal-actions city-modal-footer">
              <button type="button" class="btn-ghost" :disabled="saving" @click="dismissAdminEditor">{{ $t('city.admin.cancel') }}</button>
              <button type="button" class="btn-primary city-modal-save-btn" :disabled="saving" @click="saveAdminModal">
                {{ saving ? $t('city.admin.saving') : $t('city.admin.save') }}
              </button>
            </div>
          </template>

          <template v-else-if="activeField === 'climateMeta'">
            <div class="city-modal-body">
              <h2 class="hero-url-modal-title">{{ $t('city.admin.climateMetaTitle') }}</h2>
              <div class="city-edit-modal-stack">
                <label class="ep-label">{{ $t('city.admin.climateMetaMonthField') }}</label>
                <input ref="adminModalPrimaryInput" v-model.number="city.climate.highlightMonth" type="number" min="0" max="11" class="ep-input ep-input-full" />
                <label class="ep-label">{{ $t('city.admin.climateMetaTooltipField') }}</label>
                <input v-model="city.climate.tooltipTemp" class="ep-input ep-input-full" />
                <label class="ep-label">{{ $t('city.admin.climateMetaTempsField') }}</label>
                <textarea v-model="climateHighsDraft" class="ep-textarea" rows="3" />
              </div>
            </div>
            <div class="city-edit-modal-actions city-modal-footer">
              <button type="button" class="btn-ghost" :disabled="saving" @click="dismissAdminEditor">{{ $t('city.admin.cancel') }}</button>
              <button type="button" class="btn-primary city-modal-save-btn" :disabled="saving" @click="saveAdminModal">
                {{ saving ? $t('city.admin.saving') : $t('city.admin.save') }}
              </button>
            </div>
          </template>

          <template v-else-if="activeField === 'travelFlight'">
            <div class="city-modal-body">
              <h2 class="hero-url-modal-title">{{ $t('city.admin.travelTitle') }}</h2>
              <div class="city-edit-modal-stack">
                <label class="ep-label">{{ $t('city.admin.travelOriginField') }}</label>
                <input ref="adminModalPrimaryInput" v-model="city.travel.flightFrom" class="ep-input ep-input-full" />
                <label class="ep-label">{{ $t('city.admin.travelPriceField') }}</label>
                <input v-model.number="city.travel.flightPrice" type="number" class="ep-input ep-input-full" />
                <label class="ep-label">{{ $t('city.admin.travelDurationField') }}</label>
                <input v-model="city.travel.flightDuration" class="ep-input ep-input-full" />
                <label class="ep-label">{{ $t('city.admin.travelNoteField') }}</label>
                <input v-model="city.travel.flightNote" class="ep-input ep-input-full" />
                <label class="ep-label">{{ $t('city.admin.travelVisaField') }}</label>
                <input v-model="city.travel.visaEase" class="ep-input ep-input-full" />
                <label class="ep-label">{{ $t('city.admin.travelVisaDurationField') }}</label>
                <input v-model="city.travel.visaDuration" class="ep-input ep-input-full" />
                <label class="ep-label">{{ $t('city.admin.travelTimezoneField') }}</label>
                <input v-model="city.travel.timezoneOffset" class="ep-input ep-input-full" />
                <label class="ep-label">{{ $t('city.admin.travelTimezoneNoteField') }}</label>
                <input v-model="city.travel.timezoneNote" class="ep-input ep-input-full" />
              </div>
            </div>
            <div class="city-edit-modal-actions city-modal-footer">
              <button type="button" class="btn-ghost" :disabled="saving" @click="dismissAdminEditor">{{ $t('city.admin.cancel') }}</button>
              <button type="button" class="btn-primary city-modal-save-btn" :disabled="saving" @click="saveAdminModal">
                {{ saving ? $t('city.admin.saving') : $t('city.admin.save') }}
              </button>
            </div>
          </template>

          <template v-else-if="activeField === 'essentialApp'">
            <div class="city-modal-body">
              <h2 class="hero-url-modal-title">{{ $t('city.admin.essentialAppTitle') }}</h2>
              <p class="hero-url-modal-sub">{{ $t('city.admin.essentialAppSubLabel', { n: (essentialAppIdx ?? 0) + 1 }) }}</p>
              <div class="city-edit-modal-stack">
                <label class="ep-label" for="adm-app-name">{{ $t('city.admin.essentialAppNameField') }}</label>
                <input id="adm-app-name" ref="adminModalPrimaryInput" v-model="essentialAppDraft.name" class="ep-input ep-input-full" />
                <label class="ep-label" for="adm-app-desc">{{ $t('city.admin.essentialAppDescField') }}</label>
                <input id="adm-app-desc" v-model="essentialAppDraft.description" class="ep-input ep-input-full" />
                <label class="ep-label" for="adm-app-link">{{ $t('city.admin.essentialAppLinkField') }}</label>
                <input id="adm-app-link" v-model="essentialAppDraft.link" type="url" class="ep-input ep-input-full" placeholder="https://…" />
              </div>
            </div>
            <div class="city-edit-modal-actions city-modal-footer">
              <button type="button" class="btn-ghost" :disabled="saving" @click="dismissAdminEditor">{{ $t('city.admin.cancel') }}</button>
              <button type="button" class="btn-ghost" :disabled="saving" @click="removeEssentialApp">{{ $t('city.admin.removeApp') }}</button>
              <button type="button" class="btn-primary city-modal-save-btn" :disabled="saving" @click="saveAdminModal">
                {{ saving ? $t('city.admin.saving') : $t('city.admin.save') }}
              </button>
            </div>
          </template>

          <template v-else-if="activeField === 'visaSection'">
            <div class="city-modal-body">
              <h2 class="hero-url-modal-title">{{ $t('city.admin.visaSectionTitle') }}</h2>
              <p class="hero-url-modal-sub">{{ $t('city.admin.visaSectionSub') }}</p>
              <div class="city-edit-modal-stack">
                <label class="ep-label" for="adm-visa-steps">{{ $t('city.admin.visaStepsField') }}</label>
                <textarea id="adm-visa-steps" ref="adminModalPrimaryInput" v-model="visaStepsDraft" class="ep-textarea" rows="6" />
                <label class="ep-label" for="adm-visa-link">{{ $t('city.admin.visaLinkField') }}</label>
                <input id="adm-visa-link" v-model="visaLinkDraft" type="url" class="ep-input ep-input-full" placeholder="https://…" />
                <label class="ep-label" for="adm-visa-cost">{{ $t('city.admin.visaCostField') }}</label>
                <input id="adm-visa-cost" v-model="visaCostDraft" class="ep-input ep-input-full" />
                <label class="ep-label" for="adm-visa-diff">{{ $t('city.admin.visaDiffField') }}</label>
                <input id="adm-visa-diff" v-model="visaDifficultyDraft" class="ep-input ep-input-full" />
              </div>
            </div>
            <div class="city-edit-modal-actions city-modal-footer">
              <button type="button" class="btn-ghost" :disabled="saving" @click="dismissAdminEditor">{{ $t('city.admin.cancel') }}</button>
              <button type="button" class="btn-primary city-modal-save-btn" :disabled="saving" @click="saveAdminModal">
                {{ saving ? $t('city.admin.saving') : $t('city.admin.save') }}
              </button>
            </div>
          </template>

          <template v-else-if="activeField === 'arrival'">
            <div class="city-modal-body">
              <h2 class="hero-url-modal-title">{{ $t('city.admin.arrivalTitle') }}</h2>
              <p class="hero-url-modal-sub">{{ $t('city.admin.arrivalSub') }}</p>
              <div class="city-edit-modal-stack">
                <label class="ep-label" for="adm-arr-tips">{{ $t('city.admin.arrivalTipsField') }}</label>
                <textarea id="adm-arr-tips" ref="adminModalPrimaryInput" v-model="arrivalTipsDraft" class="ep-textarea" rows="5" />
                <label class="ep-label" for="adm-arr-docs">{{ $t('city.admin.arrivalDocsField') }}</label>
                <textarea id="adm-arr-docs" v-model="arrivalDocsDraft" class="ep-textarea" rows="5" />
              </div>
            </div>
            <div class="city-edit-modal-actions city-modal-footer">
              <button type="button" class="btn-ghost" :disabled="saving" @click="dismissAdminEditor">{{ $t('city.admin.cancel') }}</button>
              <button type="button" class="btn-primary city-modal-save-btn" :disabled="saving" @click="saveAdminModal">
                {{ saving ? $t('city.admin.saving') : $t('city.admin.save') }}
              </button>
            </div>
          </template>

          <template v-else-if="activeField === 'ctaSubtitle'">
            <div class="city-modal-body">
              <h2 class="hero-url-modal-title">{{ $t('city.admin.ctaSubtitleTitle') }}</h2>
              <textarea ref="adminModalPrimaryInput" v-model="city.ctaSubtitle" class="ep-textarea" rows="3" />
            </div>
            <div class="city-edit-modal-actions city-modal-footer">
              <button type="button" class="btn-ghost" :disabled="saving" @click="dismissAdminEditor">{{ $t('city.admin.cancel') }}</button>
              <button type="button" class="btn-primary city-modal-save-btn" :disabled="saving" @click="saveAdminModal">
                {{ saving ? $t('city.admin.saving') : $t('city.admin.save') }}
              </button>
            </div>
          </template>
        </div>
      </div>
    </Teleport>
    <Teleport to="body">
      <div
        v-if="lightboxActive"
        class="ksamil-lightbox-modal"
        role="dialog"
        aria-modal="true"
        @click.self="closeKsamilLightbox"
      >
        <button type="button" class="ksamil-lightbox-close" @click="closeKsamilLightbox">&times;</button>
        <div class="ksamil-lightbox-container">
          <img
            :src="lightboxSrc"
            :alt="lightboxCaption"
            class="ksamil-lightbox-img"
            :style="{ transform: `scale(${lightboxZoom})` }"
          />
        </div>
        <div class="ksamil-lightbox-caption">{{ lightboxCaption }}</div>
        <div class="ksamil-lightbox-controls">
          <button type="button" class="ksamil-lightbox-ctrl-btn" @click="zoomKsamilLightbox(0.15)">
            <span class="material-symbols-outlined">zoom_in</span>
            {{ currentLang === 'pt-BR' ? 'Aumentar' : 'Zoom In' }}
          </button>
          <button type="button" class="ksamil-lightbox-ctrl-btn" @click="zoomKsamilLightbox(-0.15)">
            <span class="material-symbols-outlined">zoom_out</span>
            {{ currentLang === 'pt-BR' ? 'Diminuir' : 'Zoom Out' }}
          </button>
          <button type="button" class="ksamil-lightbox-ctrl-btn" @click="resetKsamilLightboxZoom">
            <span class="material-symbols-outlined">restart_alt</span>
            {{ currentLang === 'pt-BR' ? 'Resetar' : 'Reset' }}
          </button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { useHead } from '@vueuse/head';
import { useI18n } from 'vue-i18n';
import { ref, computed } from 'vue';
import axios from "axios";
import AppTopHeader from "../components/AppTopHeader.vue";
import CityPageSkeleton from "../components/skeleton/CityPageSkeleton.vue";
import CityEssentialAppsSection from "../components/CityEssentialAppsSection.vue";
import CityCustomSectionsEditor from "@/components/CityCustomSectionsEditor.vue";
import { normalizeDestination, buildDestinationAdminPayload } from "../utils/destinationPayload";
import { fetchClimateProfile } from "../utils/openMeteoClimate";
import { toExternalHref } from "../utils/externalUrl";
import { flipmemoApiBase } from "@/config/flipmemoApi";

const MONTHS = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

/** Campos cujo editor abre em modal (não inline na página). */
const MODAL_ACTIVE_FIELDS = new Set([
  "country",
  "name",
  "tags",
  "heroVibeLine",
  "vital",
  "notFor",
  "proTip",
  "neighborhood",
  "climateLabel",
  "climateMeta",
  "travelFlight",
  "ctaSubtitle",
  "essentialApp",
  "visaSection",
  "arrival",
]);

export default {
  name: "CityPage",
  components: { AppTopHeader, CityPageSkeleton, CityEssentialAppsSection, CityCustomSectionsEditor },
  setup() {
    const { locale } = useI18n();
    const headCity = ref(null);

    const siteName = 'Nômade Digital';
    const baseUrl = 'https://nomade-digital.com';

    useHead({
      title: computed(() => {
        const c = headCity.value;
        if (!c) return siteName;
        const suffix = locale.value === 'pt-BR' ? 'Nômade Digital' : 'Digital Nomad';
        return `${c.name}, ${c.country} — ${suffix}`;
      }),
      meta: computed(() => {
        const c = headCity.value;
        const isEn = locale.value !== 'pt-BR';
        const rawDesc = (isEn && c?.descriptionEn) ? c.descriptionEn : (c?.description || '');
        const description = rawDesc.slice(0, 160);
        const ogLocale = isEn ? 'en_US' : 'pt_BR';
        const suffix = isEn ? 'Digital Nomad' : 'Nômade Digital';
        const title = c ? `${c.name}, ${c.country} — ${suffix}` : siteName;
        const ogUrl = c ? `${baseUrl}/city/${c.slug}` : baseUrl;
        const ogImage = (c?.heroImages?.[0]) || (c?.imageUrl) || `${baseUrl}/logo.svg`;

        const robots = (isEn && c && !c.enTranslated) ? 'noindex, follow' : 'index, follow';

        return [
          { name: 'description', content: description },
          { name: 'robots', content: robots },
          { property: 'og:title', content: title },
          { property: 'og:description', content: description },
          { property: 'og:image', content: ogImage },
          { property: 'og:locale', content: ogLocale },
          { property: 'og:url', content: ogUrl },
          { property: 'og:type', content: 'article' },
        ];
      }),
      link: computed(() => {
        const c = headCity.value;
        const canonical = c ? `${baseUrl}/city/${c.slug}` : baseUrl;
        return [
          { rel: 'canonical', href: canonical },
          { rel: 'alternate', hreflang: 'pt', href: canonical },
          { rel: 'alternate', hreflang: 'en', href: canonical },
          { rel: 'alternate', hreflang: 'x-default', href: canonical },
        ];
      }),
    });

    return { headCity };
  },
  data() {
    return {
      activeNavId: "scores",
      navScrollHandler: null,
      essentialAppIdx: null,
      essentialAppDraft: { name: "", description: "", link: "" },
      visaStepsDraft: "",
      visaLinkDraft: "",
      visaCostDraft: "",
      visaDifficultyDraft: "",
      arrivalTipsDraft: "",
      arrivalDocsDraft: "",
      city: null,
      loading: false,
      saving: false,
      isAdmin: false,
      token: null,
      currentUserId: null,
      activeField: null,
      activeHeroIdx: null,
      heroDraft: "",
      heroUploadBusy: false,
      tagsDraft: "",
      budgetEditKey: null,
      budgetDraft: null,
      insightEdit: null,
      insightDraft: { title: "", body: "" },
      vitalDraft: { label: "", value: 0, max: 10, display: "" },
      vitalIdx: null,
      nbDraft: { name: "", description: "", image: "" },
      nbIdx: null,
      notForDraft: "",
      climateHighsDraft: "",
      climateFetchBusy: false,
      climateAutoNote: "",
      pageEscHandler: null,
      lightboxActive: false,
      lightboxSrc: "",
      lightboxCaption: "",
      lightboxZoom: 1,
    };
  },
  computed: {
    editMode() {
      return this.isAdmin && this.$route.query.edit === "1";
    },
    showAdminEditorModal() {
      if (!this.editMode || !this.city) return false;
      if (this.insightEdit) return true;
      if (this.budgetEditKey) return true;
      const f = this.activeField;
      return f != null && MODAL_ACTIVE_FIELDS.has(f);
    },
    budgetEditLabel() {
      const key = this.budgetEditKey;
      const map = {
        rent: "city.costs.rent",
        food: "city.costs.food",
        coworking: "city.costs.coworking",
        transport: "city.costs.transport",
      };
      return map[key] ? this.$t(map[key]) : (key || "");
    },
    loginReturnPath() {
      return `/login?redirect=${encodeURIComponent(this.$route.fullPath)}`;
    },
    totalGalleryImages() {
      if (!this.city) return 0;
      return (this.city.heroImages || []).filter(Boolean).length;
    },
    hasAnyGalleryImage() {
      return this.totalGalleryImages > 0;
    },
    gallerySlots() {
      if (!this.city) return [];
      const imgs = [...(this.city.heroImages || [])];
      while (imgs.length < 3) imgs.push("");
      return imgs.slice(0, 3);
    },
    highlightMonthIdx() {
      const hm = this.city?.climate?.highlightMonth;
      if (hm == null || Number.isNaN(Number(hm))) return 4;
      const n = Number(hm);
      return Math.max(0, Math.min(11, n));
    },
    monthBars() {
      const highs = this.city?.climate?.monthlyHighs || [];
      const vals = [];
      for (let i = 0; i < 12; i++) {
        let v = highs[i] != null ? Number(highs[i]) : NaN;
        if (Number.isNaN(v)) v = 12 + i * 0.8;
        v = Math.min(55, Math.max(-30, v));
        vals.push(v);
      }
      const max = Math.max(...vals);
      const min = Math.min(...vals);
      const CHART_INNER = 176;
      const span = max - min;
      return vals.map((value, i) => {
        let heightPx;
        if (span < 0.25) {
          heightPx = Math.round((value / Math.max(max, 1)) * CHART_INNER);
        } else {
          heightPx = Math.round(((value - min) / span) * CHART_INNER);
        }
        heightPx = Math.max(6, Math.min(CHART_INNER, heightPx));
        const rounded = Math.round(value * 10) / 10;
        const tempLabel = Number.isInteger(rounded) ? `${rounded}°` : `${rounded.toFixed(1)}°`.replace(/\.0°$/, "°");
        return {
          label: MONTHS[i],
          value,
          heightPx,
          tempLabel,
        };
      });
    },
    localTimeHint() {
      return new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
    },
    visaStepsDisplay() {
      if (!this.city?.visaSection) return [];
      return (this.city.visaSection.visaSteps || []).map((s) => String(s || "").trim()).filter(Boolean);
    },
    arrivalTipsDisplay() {
      if (!this.city?.arrival) return [];
      return (this.city.arrival.arrivalTips || []).map((s) => String(s || "").trim()).filter(Boolean);
    },
    arrivalDocsDisplay() {
      if (!this.city?.arrival) return [];
      return (this.city.arrival.requiredDocuments || []).map((s) => String(s || "").trim()).filter(Boolean);
    },
    navShowApps() {
      if (!this.city) return false;
      const n = (this.city.essentialApps || []).filter((a) => (a?.name || "").trim()).length;
      return n >= 1 || this.editMode;
    },
    navShowVisa() {
      if (!this.city) return false;
      return this.visaStepsDisplay.length > 0 || this.editMode;
    },
    navShowCustomSections() {
      return Array.isArray(this.city?.customSections) && this.city.customSections.length > 0;
    },
    isKsamil() {
      return this.city && (this.city.id === 15 || (this.city.slug && this.city.slug.toLowerCase() === 'ksamil') || (this.city.name && this.city.name.toLowerCase() === 'ksamil'));
    },
    currentLang() {
      return this.$i18n?.locale || localStorage.getItem('flipmemo_lang') || 'pt-BR';
    },
    localizedDescription() {
      if (!this.city) return '';
      if (this.currentLang !== 'pt-BR' && this.city.descriptionEn) return this.city.descriptionEn;
      return this.city.description || '';
    },
    localizedPros() {
      if (!this.city) return [];
      if (this.currentLang !== 'pt-BR' && this.city.prosEn?.length) return this.city.prosEn;
      return this.city.pros || [];
    },
    localizedCons() {
      if (!this.city) return [];
      if (this.currentLang !== 'pt-BR' && this.city.consEn?.length) return this.city.consEn;
      return this.city.cons || [];
    },
    navShowArrival() {
      if (!this.city) return false;
      return this.arrivalTipsDisplay.length + this.arrivalDocsDisplay.length > 0 || this.editMode;
    },
    showVisaSection() {
      return this.navShowVisa;
    },
    showArrivalSection() {
      return this.navShowArrival;
    },
    visaCtaHref() {
      if (!this.city?.visaSection?.visaLink) return "";
      return toExternalHref(this.city.visaSection.visaLink);
    },
  },
  watch: {
    "$route.params.id": "fetchCity",
    "$route.query.edit": "onEditQuery",
    activeHeroIdx: "syncPageOverlay",
    activeField: "syncPageOverlay",
    insightEdit: "syncPageOverlay",
    budgetEditKey: "syncPageOverlay",
    editMode() {
      this.syncPageOverlay();
      this.$nextTick(() => this.updateActiveNavFromScroll());
    },
    city: {
      handler(val) {
        this.headCity = val;
        if (!val) {
          this.teardownNavScrollSpy();
          return;
        }
        this.$nextTick(() => {
          this.setupNavScrollSpy();
          this.updateActiveNavFromScroll();
        });
      },
    },
    showAdminEditorModal(val) {
      if (val) {
        this.$nextTick(() => {
          const el = this.$refs.adminModalPrimaryInput;
          if (el && typeof el.focus === "function") el.focus();
        });
      }
    },
  },
  mounted() {
    this.token = localStorage.getItem("token");
    const storedUid = Number(localStorage.getItem("userId"));
    if (Number.isFinite(storedUid)) this.currentUserId = storedUid;
    this.fetchUserAdmin();
    this.fetchCity();
  },
  beforeUnmount() {
    this.teardownNavScrollSpy();
    this.finishHeroUrlModal();
    this.insightEdit = null;
    this.budgetEditKey = null;
    this.activeField = null;
    if (this.pageEscHandler) {
      window.removeEventListener("keydown", this.pageEscHandler);
      this.pageEscHandler = null;
    }
    document.body.style.overflow = "";
  },
  methods: {
    openKsamilLightbox(src, caption) {
      this.lightboxSrc = src;
      this.lightboxCaption = caption;
      this.lightboxActive = true;
      this.lightboxZoom = 1;
      document.body.style.overflow = "hidden";
    },
    closeKsamilLightbox() {
      this.lightboxActive = false;
      document.body.style.overflow = "";
    },
    zoomKsamilLightbox(amount) {
      let z = this.lightboxZoom + amount;
      if (z < 0.4) z = 0.4;
      if (z > 3.5) z = 3.5;
      this.lightboxZoom = z;
    },
    resetKsamilLightboxZoom() {
      this.lightboxZoom = 1;
    },
    apiBase() {
      return flipmemoApiBase;
    },
    authHeader() {
      return this.token ? { Authorization: `Bearer ${this.token}` } : {};
    },
    async fetchUserAdmin() {
      this.token = localStorage.getItem("token");
      if (!this.token) {
        this.isAdmin = false;
        this.currentUserId = null;
        this.onEditQuery();
        return;
      }
      try {
        const { data } = await axios.get(`${this.apiBase()}/user`, { headers: this.authHeader() });
        this.isAdmin = !!data.IsAdmin;
        const fromApi = data.Id != null ? data.Id : data.id;
        const fromStorage = Number(localStorage.getItem("userId"));
        this.currentUserId = fromApi != null ? fromApi : Number.isFinite(fromStorage) ? fromStorage : null;
      } catch {
        this.isAdmin = false;
        const fromStorage = Number(localStorage.getItem("userId"));
        this.currentUserId = Number.isFinite(fromStorage) ? fromStorage : null;
      }
      this.onEditQuery();
    },
    onEditQuery() {
      if (this.$route.query.edit === "1" && !this.isAdmin) {
        this.$router.replace({ path: this.$route.path, query: {} });
      }
    },
    exitEditMode() {
      this.finishHeroUrlModal();
      this.insightEdit = null;
      this.budgetEditKey = null;
      this.activeField = null;
      this.essentialAppIdx = null;
      this.vitalIdx = null;
      this.nbIdx = null;
      this.syncPageOverlay();
      this.$router.replace({ path: this.$route.path, query: {} });
      this.$nextTick(() => this.updateActiveNavFromScroll());
    },
    syncPageOverlay() {
      const anyOpen = this.activeHeroIdx !== null || this.showAdminEditorModal;
      if (anyOpen) {
        document.body.style.overflow = "hidden";
        if (!this.pageEscHandler) {
          this.pageEscHandler = (e) => {
            if (e.key !== "Escape") return;
            if (this.activeHeroIdx !== null) this.cancelHeroEdit();
            else this.dismissAdminEditor();
          };
          window.addEventListener("keydown", this.pageEscHandler);
        }
      } else {
        document.body.style.overflow = "";
        if (this.pageEscHandler) {
          window.removeEventListener("keydown", this.pageEscHandler);
          this.pageEscHandler = null;
        }
      }
    },
    dismissAdminEditor() {
      if (this.activeHeroIdx !== null) {
        this.cancelHeroEdit();
        return;
      }
      if (this.insightEdit) {
        this.insightEdit = null;
        return;
      }
      if (this.budgetEditKey) {
        this.budgetEditKey = null;
        return;
      }
      if (this.activeField === "vital") {
        this.cancelVitalEdit();
        return;
      }
      this.nbIdx = null;
      if (this.activeField === "essentialApp") {
        this.essentialAppIdx = null;
      }
      this.activeField = null;
    },
    setupNavScrollSpy() {
      this.teardownNavScrollSpy();
      this.navScrollHandler = () => this.updateActiveNavFromScroll();
      window.addEventListener("scroll", this.navScrollHandler, { passive: true });
      this.updateActiveNavFromScroll();
    },
    teardownNavScrollSpy() {
      if (this.navScrollHandler) {
        window.removeEventListener("scroll", this.navScrollHandler);
        this.navScrollHandler = null;
      }
    },
    updateActiveNavFromScroll() {
      if (!this.city) return;
      let order = [];
      if (this.isKsamil) {
        order = ["scores", "costs", "insights", "climate", "arrival", "logistics", "mobility", "leisure"];
      } else {
        order = [
          "scores",
          "costs",
          "insights",
          "neighborhoods",
          "climate",
          "essential-apps",
          "visa",
          "arrival",
        ];
        // Append custom section anchors
        if (Array.isArray(this.city?.customSections)) {
          for (const s of this.city.customSections) {
            if (s.anchorSlug) order.push(s.anchorSlug);
          }
        }
      }
      const offset = 132;
      let active = "scores";
      for (const id of order) {
        if (!this.isKsamil) {
          if (id === "essential-apps" && !this.navShowApps) continue;
          if (id === "visa" && !this.navShowVisa) continue;
          if (id === "arrival" && !this.navShowArrival) continue;
        }
        const el = document.getElementById(id);
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        if (top <= offset) active = id;
      }
      if (this.activeNavId !== active) this.activeNavId = active;
    },
    formatScore(v) {
      const n = Number(v);
      if (Number.isNaN(n)) return "—";
      return n.toFixed(1);
    },
    formatMoney(v) {
      if (v == null || v === "" || Number.isNaN(Number(v))) return "—";
      return Number(v).toLocaleString("pt-BR");
    },
    formatDate(iso) {
      if (!iso) return "";
      try {
        return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
      } catch {
        return iso;
      }
    },
    scrollTo(id) {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        this.activeNavId = id;
      }
    },
    startEditEssentialApp(idx) {
      this.essentialAppIdx = idx;
      const a = this.city.essentialApps[idx] || {};
      this.essentialAppDraft = {
        name: a.name || "",
        description: a.description || "",
        link: a.link || "",
      };
      this.activeField = "essentialApp";
    },
    addEssentialApp() {
      if (!this.city.essentialApps) this.city.essentialApps = [];
      this.city.essentialApps.push({ name: "", description: "", link: "" });
      this.startEditEssentialApp(this.city.essentialApps.length - 1);
    },
    applyEssentialAppDraft() {
      if (this.essentialAppIdx == null || !this.city.essentialApps) return;
      const cur = [...this.city.essentialApps];
      cur[this.essentialAppIdx] = { ...this.essentialAppDraft };
      this.city.essentialApps = cur;
      this.activeField = null;
      this.essentialAppIdx = null;
    },
    removeEssentialApp() {
      if (this.essentialAppIdx == null) return;
      this.city.essentialApps.splice(this.essentialAppIdx, 1);
      this.activeField = null;
      this.essentialAppIdx = null;
    },
    startEditVisaSection() {
      const v = this.city.visaSection || {
        visaSteps: [],
        visaLink: "",
        visaCost: "",
        visaDifficulty: "",
      };
      this.visaStepsDraft = (v.visaSteps || []).join("\n");
      this.visaLinkDraft = v.visaLink || "";
      this.visaCostDraft = v.visaCost || "";
      this.visaDifficultyDraft = v.visaDifficulty || "";
      this.activeField = "visaSection";
    },
    applyVisaSectionDraft() {
      if (!this.city.visaSection) {
        this.city.visaSection = { visaSteps: [], visaLink: "", visaCost: "", visaDifficulty: "" };
      }
      const lines = String(this.visaStepsDraft || "")
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean);
      this.city.visaSection.visaSteps = lines;
      this.city.visaSection.visaLink = String(this.visaLinkDraft || "").trim();
      this.city.visaSection.visaCost = String(this.visaCostDraft || "").trim();
      this.city.visaSection.visaDifficulty = String(this.visaDifficultyDraft || "").trim();
      this.activeField = null;
    },
    startEditArrival() {
      const a = this.city.arrival || { arrivalTips: [], requiredDocuments: [] };
      this.arrivalTipsDraft = (a.arrivalTips || []).join("\n");
      this.arrivalDocsDraft = (a.requiredDocuments || []).join("\n");
      this.activeField = "arrival";
    },
    applyArrivalDraft() {
      if (!this.city.arrival) this.city.arrival = { arrivalTips: [], requiredDocuments: [] };
      this.city.arrival.arrivalTips = String(this.arrivalTipsDraft || "")
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean);
      this.city.arrival.requiredDocuments = String(this.arrivalDocsDraft || "")
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean);
      this.activeField = null;
    },
    galleryClass(i) {
      if (i === 0) return "g-main";
      return "g-side";
    },
    showBudget(key) {
      if (!this.city?.budget) return false;
      const v = this.city.budget[key];
      if (this.editMode) return true;
      return v != null && !Number.isNaN(Number(v)) && Number(v) > 0;
    },
    barWidth(st) {
      const max = Number(st.max) > 0 ? Number(st.max) : 10;
      const v = Number(st.value) || 0;
      return Math.min(100, Math.round((v / max) * 100));
    },
    statDisplay(st) {
      if (st.display) return st.display;
      const max = Number(st.max) > 0 ? Number(st.max) : 10;
      return `${st.value}/${max}`;
    },
    padIdx(i) {
      return String(i + 1).padStart(2, "0");
    },
    startEdit(field) {
      if (!this.editMode) return;
      this.activeField = field;
      if (field === "tags") {
        this.tagsDraft = (this.city.tags || []).join(", ");
      }
      if (field === "notFor") {
        this.notForDraft = (this.city.notFor || []).join("\n");
      }
      if (field === "climateMeta") {
        const h = this.city.climate?.monthlyHighs || [];
        this.climateHighsDraft = h.join(", ");
      }
    },
    finishHeroUrlModal() {
      this.activeHeroIdx = null;
      this.heroDraft = "";
      this.syncPageOverlay();
    },
    startEditHero(idx) {
      this.activeHeroIdx = idx;
      const imgs = this.gallerySlots;
      this.heroDraft = imgs[idx] || "";
      this.$nextTick(() => this.$refs.heroUrlModalInput?.focus?.());
    },
    cancelHeroEdit() {
      this.finishHeroUrlModal();
    },
    async handleHeroUpload(event) {
      const file = event.target.files?.[0];
      if (!file) return;

      this.heroUploadBusy = true;
      const formData = new FormData();
      formData.append("file", file);

      try {
        const { data } = await axios.post(
          `${this.apiBase()}/uploads?folder=destination`,
          formData,
          {
            headers: {
              ...this.authHeader(),
              "Content-Type": "multipart/form-data",
            },
          }
        );
        if (data && data.url) {
          this.heroDraft = data.url;
        }
      } catch (err) {
        window.alert(err.response?.data?.error || "Falha ao enviar arquivo.");
      } finally {
        this.heroUploadBusy = false;
        event.target.value = "";
      }
    },
    applyHeroDraft(idx) {
      const url = String(this.heroDraft || "").trim();
      const slots = ["", "", ""];
      const cur = this.city.heroImages || [];
      for (let i = 0; i < 3; i += 1) {
        const existing = String(cur[i] || "").trim();
        slots[i] = i === idx ? url : existing;
      }
      let compact = [...slots];
      while (compact.length > 0 && !compact[compact.length - 1]) compact.pop();
      this.city.heroImages = compact.length ? [...compact] : [];
      const first = compact.find((u) => u);
      if (first) this.city.imageUrl = first;
      else if (!compact.length) this.city.imageUrl = "";
      this.finishHeroUrlModal();
    },
    async fetchClimateAuto() {
      if (!this.city || this.climateFetchBusy) return;
      this.climateFetchBusy = true;
      this.climateAutoNote = "";
      try {
        const r = await fetchClimateProfile(this.city.name, this.city.country);
        if (!this.city.climate) this.city.climate = {};
        this.city.climate.monthlyHighs = [...r.monthlyHighs];
        this.city.climate.bestTimeLabel = r.bestTimeLabel;
        this.city.climate.highlightMonth = r.highlightMonth;
        this.city.climate.tooltipTemp = r.tooltipTemp;
        this.climateAutoNote = `Open-Meteo: médias de temperatura máxima diária em ${r.sourceYear} (não comercial, uso razoável). Ajuste manual se quiser outro critério.`;
      } catch (e) {
        const msg = e?.message || "Não foi possível buscar o clima.";
        window.alert(msg);
      } finally {
        this.climateFetchBusy = false;
      }
    },
    applyTagsDraft() {
      this.city.tags = this.tagsDraft
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
      this.activeField = null;
    },
    startEditBudget(key) {
      this.budgetEditKey = key;
      const v = this.city.budget[key];
      this.budgetDraft = v != null ? Number(v) : null;
    },
    applyBudgetDraft() {
      if (this.budgetEditKey) {
        this.city.budget[this.budgetEditKey] = this.budgetDraft === "" || this.budgetDraft == null ? null : Number(this.budgetDraft);
      }
      this.budgetEditKey = null;
    },
    startEditInsight(list, idx) {
      this.insightEdit = { list, idx };
      const item = this.city[list][idx] || { title: "", body: "" };
      this.insightDraft = { title: item.title || "", body: item.body || "" };
    },
    applyInsightDraft() {
      const { list, idx } = this.insightEdit || {};
      if (!list || idx == null) return;
      const cur = [...this.city[list]];
      cur[idx] = { ...this.insightDraft };
      this.city[list] = cur;
      this.insightEdit = null;
    },
    removeCurrentInsight() {
      const { list, idx } = this.insightEdit || {};
      if (!list || idx == null) return;
      this.city[list].splice(idx, 1);
      this.insightEdit = null;
    },
    addInsight(list) {
      this.city[list].push({ title: "", body: "Novo item" });
      this.startEditInsight(list, this.city[list].length - 1);
    },
    startEditVital(si) {
      this.vitalIdx = si;
      const st = this.city.vitalStats[si] || {};
      this.vitalDraft = {
        label: st.label || "",
        value: Number(st.value) || 0,
        max: Number(st.max) || 10,
        display: st.display || "",
      };
      this.activeField = "vital";
    },
    applyVitalFromDraft() {
      if (this.vitalIdx == null) return;
      const st = this.city.vitalStats[this.vitalIdx];
      if (st) Object.assign(st, this.vitalDraft);
      this.activeField = null;
      this.vitalIdx = null;
    },
    cancelVitalEdit() {
      this.activeField = null;
      this.vitalIdx = null;
    },
    startEditNeighborhood(ni) {
      this.nbIdx = ni;
      const n = this.city.neighborhoods[ni] || {};
      this.nbDraft = { name: n.name || "", description: n.description || "", image: n.image || "" };
      this.activeField = "neighborhood";
    },
    applyNeighborhoodDraft() {
      if (this.nbIdx == null) return;
      const cur = [...this.city.neighborhoods];
      cur[this.nbIdx] = { ...this.nbDraft };
      this.city.neighborhoods = cur;
      this.activeField = null;
      this.nbIdx = null;
    },
    removeCurrentNeighborhood() {
      if (this.nbIdx == null) return;
      this.city.neighborhoods.splice(this.nbIdx, 1);
      this.activeField = null;
      this.nbIdx = null;
    },
    addNeighborhood() {
      this.city.neighborhoods.push({ name: "Novo bairro", description: "", image: "" });
      this.startEditNeighborhood(this.city.neighborhoods.length - 1);
    },
    applyNotForDraft() {
      this.city.notFor = this.notForDraft
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean);
      this.activeField = null;
    },
    applyClimateHighs() {
      if (!this.city.climate) this.city.climate = {};
      const parts = this.climateHighsDraft.split(",").map((s) => Number(String(s).trim())).filter((n) => !Number.isNaN(n));
      this.city.climate.monthlyHighs = parts.slice(0, 12);
      this.activeField = null;
    },
    async fetchCity() {
      this.loading = true;
      try {
        const response = await axios.get(`${this.apiBase()}/destinations/${this.$route.params.id}`, {
          headers: this.authHeader(),
        });
        this.city = normalizeDestination(response.data);
        if (this.city.slug && this.$route.params.id !== this.city.slug) {
          this.$router.replace(`/city/${this.city.slug}`);
        }
      } catch {
        this.city = null;
      } finally {
        this.loading = false;
      }
    },
    async toggleSave() {
      if (!this.token) {
        localStorage.setItem("preLoginRoute", this.$route.fullPath);
        this.$router.push("/login");
        return;
      }
      if (!this.city) return;
      this.saving = true;
      try {
        if (this.city.isSaved) {
          await axios.delete(`${this.apiBase()}/saved-destinations/${this.city.id}`, { headers: this.authHeader() });
          this.city.isSaved = false;
        } else {
          await axios.post(`${this.apiBase()}/saved-destinations/${this.city.id}`, {}, { headers: this.authHeader() });
          this.city.isSaved = true;
        }
      } finally {
        this.saving = false;
      }
    },
    async saveAdminModal() {
      if (!this.city || !this.token || !this.isAdmin) {
        window.alert("Faça login como administrador para salvar.");
        return;
      }
      const hadSimpleField =
        this.activeField != null &&
        !this.insightEdit &&
        !this.budgetEditKey &&
        !["tags", "notFor", "vital", "neighborhood", "climateMeta"].includes(this.activeField);

      if (this.insightEdit) {
        this.applyInsightDraft();
      } else if (this.budgetEditKey) {
        this.applyBudgetDraft();
      } else if (this.activeField === "tags") {
        this.applyTagsDraft();
      } else if (this.activeField === "notFor") {
        this.applyNotForDraft();
      } else if (this.activeField === "vital") {
        this.applyVitalFromDraft();
      } else if (this.activeField === "neighborhood") {
        this.applyNeighborhoodDraft();
      } else if (this.activeField === "climateMeta") {
        this.applyClimateHighs();
      } else if (this.activeField === "essentialApp") {
        this.applyEssentialAppDraft();
      } else if (this.activeField === "visaSection") {
        this.applyVisaSectionDraft();
      } else if (this.activeField === "arrival") {
        this.applyArrivalDraft();
      }

      const ok = await this.saveDestinationAdmin();
      if (ok && hadSimpleField) {
        this.activeField = null;
      }
    },
    async saveHeroModal() {
      if (!this.city || !this.token || !this.isAdmin) {
        window.alert("Faça login como administrador para salvar.");
        return;
      }
      const idx = this.activeHeroIdx;
      if (idx == null) return;
      this.applyHeroDraft(idx);
      await this.saveDestinationAdmin();
    },
    async saveDestinationAdmin() {
      if (!this.city || !this.token || !this.isAdmin) return false;
      const payload = buildDestinationAdminPayload(this.city);
      if (!payload || payload.monthlyCost <= 0) {
        window.alert("Custo mensal deve ser maior que zero.");
        return false;
      }
      this.saving = true;
      try {
        const { data } = await axios.put(`${this.apiBase()}/admin/destinations/${this.city.id}`, payload, {
          headers: this.authHeader(),
        });
        this.city = normalizeDestination({ ...data, isSaved: this.city.isSaved });
        return true;
      } catch (e) {
        window.alert(e.response?.data?.error || "Falha ao salvar");
        return false;
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.city-page {
  --surface: #0d1b2e;
  --on-surface: #e2eaf4;
  --primary: #4f8ef7;
  --primary-container: #1e3a8a;
  --on-primary: #fff;
  --surface-low: #081526;
  --surface-lowest: #152338;
  --outline: #4a6080;
  --on-variant: #94adc8;
  --secondary: #34d399;
  --secondary-container: #064e3b;
  --on-secondary-container: #6ee7b7;
  --error: #f87171;
  --error-container: #450a0a;
  --tertiary-fixed: #431407;
  --on-tertiary-fixed: #fed7aa;
  --primary-fixed: #1e3a8a;
  --on-primary-fixed: #bfdbfe;
  min-height: 100vh;
  background: var(--surface);
  color: var(--on-surface);
  font-family: "Inter", "Poppins", system-ui, sans-serif;
  padding-bottom: 100px;
}
/* Sticky CTA padding removed */

.admin-edit-bar {
  position: fixed;
  top: 66px;
  left: 0;
  right: 0;
  z-index: 45;
  background: #1e293b;
  color: #f8fafc;
  padding: 10px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 0.875rem;
}
.admin-edit-bar-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.admin-btn-leave {
  padding: 10px 16px;
  border-radius: 10px;
  font-weight: 800;
  font-size: 0.8rem;
  cursor: pointer;
  border: 2px solid rgba(255, 255, 255, 0.55);
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.2);
}
.admin-btn-leave:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.24);
  border-color: #fff;
}
.admin-btn-save {
  padding: 10px 18px;
  border-radius: 10px;
  font-weight: 800;
  font-size: 0.8rem;
  cursor: pointer;
  border: none;
  background: #38bdf8;
  color: #0f172a;
  box-shadow: 0 2px 12px rgba(56, 189, 248, 0.45);
}
.admin-btn-save:hover:not(:disabled) {
  filter: brightness(1.06);
}
.admin-btn-save:disabled,
.admin-btn-leave:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.edit-mode .city-hero-wrap {
  padding-top: 114px;
}

.city-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}
.city-loading {
  padding: 120px 24px;
  text-align: center;
  color: var(--on-variant);
}

.city-hero-wrap {
  padding-top: 90px;
  padding-bottom: 32px;
}
.hero-head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 32px;
}
.country-line {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--primary);
  font-weight: 600;
  margin: 0 0 8px;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}
.country-pin {
  font-size: 16px !important;
}
.city-title {
  margin: 0;
  font-size: clamp(2.5rem, 6vw, 4.5rem);
  font-weight: 900;
  letter-spacing: -0.04em;
  line-height: 1;
}
.tags-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
  align-items: center;
}
.tag-pill {
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 0.65rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  background: #1a2e48;
  color: var(--on-variant);
  border: 1px solid #1e3352;
}
.tag-pill:nth-child(4n + 4) {
  background: var(--secondary-container);
  color: var(--on-secondary-container);
}
.score-box {
  text-align: right;
}
.score-num {
  font-size: clamp(3rem, 8vw, 3.75rem);
  font-weight: 900;
  color: var(--primary);
  line-height: 1;
}
.score-label {
  font-size: 0.65rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--outline);
  margin-top: 6px;
}

.hero-gallery {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  min-height: 180px;
  max-height: min(46vh, 300px);
}
@media (min-width: 900px) {
  .hero-gallery {
    grid-template-columns: repeat(12, 1fr);
    grid-template-rows: 1fr 1fr;
    gap: 12px;
    min-height: 220px;
    max-height: min(38vh, 340px);
  }
  /* :first-of-type em .g-side não batia no 2º quadrado (o 1º div é g-main). Usar ordem explícita. */
  .hero-gallery > .gallery-cell:nth-of-type(1).g-main {
    grid-column: 1 / span 8;
    grid-row: 1 / span 2;
  }
  .hero-gallery > .gallery-cell:nth-of-type(2) {
    grid-column: 9 / span 4;
    grid-row: 1;
    min-height: 0;
  }
  .hero-gallery > .gallery-cell:nth-of-type(3) {
    grid-column: 9 / span 4;
    grid-row: 2;
    min-height: 0;
  }
}
.gallery-cell {
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  min-height: 100px;
  background: var(--surface-low);
}
.hero-gallery > .gallery-cell.g-full {
  grid-column: 1 / span 12 !important;
  grid-row: 1 / span 2 !important;
}
.mobile-sticky-nav {
  display: none;
}
@media (max-width: 1023px) {
  .mobile-sticky-nav {
    display: block;
    position: sticky;
    top: 66px;
    z-index: 40;
    background: rgba(13, 27, 46, 0.85);
    backdrop-filter: blur(12px);
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    margin-bottom: 24px;
  }
  .mobile-nav-scroll {
    display: flex;
    overflow-x: auto;
    white-space: nowrap;
    padding: 12px 24px;
    gap: 20px;
    -webkit-overflow-scrolling: touch;
  }
  .mobile-nav-scroll::-webkit-scrollbar {
    display: none;
  }
  .mobile-nav-scroll a {
    color: var(--on-variant);
    text-decoration: none;
    font-size: 0.85rem;
    font-weight: 700;
    transition: color 0.2s;
  }
  .mobile-nav-scroll a:hover,
  .mobile-nav-scroll a.is-active {
    color: var(--primary);
  }
  .city-sidebar {
    display: none;
  }
}
.gallery-img {
  width: 100%;
  height: 100%;
  min-height: 100px;
  object-fit: cover;
  display: block;
}
.gallery-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 100px;
  color: var(--outline);
  font-size: 0.875rem;
}
.ep-img-wrap {
  position: relative;
  height: 100%;
  min-height: inherit;
}
.ep-img-btn {
  position: absolute;
  bottom: 12px;
  right: 12px;
  opacity: 0;
  transition: opacity 0.15s;
  z-index: 2;
  font-size: 0.65rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 8px 10px;
  border: none;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.92);
  color: var(--primary);
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}
.edit-mode .ep-img-btn {
  opacity: 1;
}
.edit-mode .ep-img-wrap:hover .ep-img-btn {
  opacity: 1;
  background: #fff;
}
.hero-url-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(4px);
}
.hero-url-modal {
  width: min(100%, 520px);
  padding: 0;
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.2);
  border: 1px solid rgba(226, 232, 240, 0.9);
}
.city-modal-flex {
  display: flex;
  flex-direction: column;
  max-height: min(88vh, 640px);
  overflow: hidden;
}
.city-modal-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 24px 24px 12px;
  -webkit-overflow-scrolling: touch;
}
.city-modal-footer {
  flex-shrink: 0;
  margin-top: 0 !important;
  padding: 14px 24px 18px !important;
  border-top: 1px solid #e2e8f0;
  background: #fff;
  box-shadow: 0 -4px 20px rgba(255, 255, 255, 0.95);
}
.hero-url-modal-title {
  margin: 0 0 8px;
  font-size: 1.25rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #0f172a;
}
.hero-url-modal-sub {
  margin: 0 0 18px;
  font-size: 0.875rem;
  color: #64748b;
  line-height: 1.45;
}
.hero-url-modal-label {
  display: block;
  margin-bottom: 8px;
  font-size: 0.7rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;
}
.hero-url-modal-input {
  margin-bottom: 20px;
}
.hero-url-modal-actions,
.city-edit-modal-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-end;
  align-items: center;
}
.hero-url-modal.city-edit-modal--wide {
  width: min(100%, 640px);
}
.city-edit-modal-stack .ep-label {
  display: block;
  margin-top: 14px;
}
.city-edit-modal-stack .ep-label:first-of-type {
  margin-top: 0;
}
.climate-admin-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
}
.btn-climate-fetch {
  padding: 10px 16px;
  border-radius: 10px;
  font-weight: 800;
  font-size: 0.75rem;
  border: none;
  cursor: pointer;
  background: #0ea5e9;
  color: #fff;
}
.btn-climate-fetch:hover:not(:disabled) {
  filter: brightness(1.05);
}
.btn-climate-fetch:disabled {
  opacity: 0.65;
  cursor: wait;
}
.climate-source-note {
  margin-top: 10px;
  max-width: 52rem;
}

.vibe-row {
  margin-top: 20px;
}
.vibe-card {
  display: inline-flex;
  flex-direction: column;
  gap: 6px;
  padding: 16px 20px;
  background: rgba(21, 35, 56, 0.82);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 16px;
  max-width: 100%;
}
.vibe-label {
  font-size: 0.65rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--on-variant);
}
.vibe-text {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  flex-wrap: wrap;
}
.vibe-sun {
  color: var(--secondary) !important;
}

.city-layout {
  display: grid;
  grid-template-columns: 1fr;
  gap: 48px;
  padding-bottom: 48px;
}
@media (min-width: 1024px) {
  .city-layout {
    grid-template-columns: 280px 1fr;
    align-items: start;
  }
  .city-sidebar {
    position: sticky;
    top: 96px;
  }
}
.side-card {
  background: var(--surface-low);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 20px;
}
.side-title {
  font-size: 0.65rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--on-variant);
  margin: 0 0 16px;
}
.side-nav {
  list-style: none;
  margin: 0;
  padding: 0;
}
.side-nav li {
  margin-bottom: 14px;
}
.side-nav a {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--on-variant);
  text-decoration: none;
}
.side-nav a:hover {
  color: var(--primary);
}
.side-nav a.is-active {
  color: var(--primary);
  font-weight: 800;
}
.side-nav .material-symbols-outlined {
  font-size: 20px !important;
}

.city-main {
  display: flex;
  flex-direction: column;
  gap: 80px;
  min-width: 0;
}
.section-block.scroll-target {
  scroll-margin-top: 100px;
}
.community-local-section .section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}
.community-local-desc {
  margin: 0 0 16px;
  line-height: 1.5;
  color: var(--on-variant);
}
.community-local-actions .community-hub-cta {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
}
.community-toggle input {
  margin-right: 8px;
  vertical-align: middle;
}
.section-title {
  font-size: 1.75rem;
  font-weight: 900;
  letter-spacing: -0.02em;
  margin: 0 0 24px;
}
.section-title.tight {
  margin-bottom: 4px;
}
.section-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 24px;
}
.section-head .section-title {
  margin: 0;
}
.currency-pill {
  font-size: 0.65rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  background: #1a2e48;
  border: 1px solid #1e3352;
  color: var(--on-variant);
  padding: 6px 16px;
  border-radius: 999px;
}

.vital-card {
  background: var(--surface-lowest);
  border-radius: 24px;
  padding: 32px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  display: grid;
  gap: 22px;
}
.vital-row {
  position: relative;
}
.vital-head {
  display: flex;
  justify-content: space-between;
  font-size: 0.65rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--on-variant);
  margin-bottom: 8px;
}
.vital-bar-bg {
  height: 8px;
  background: #eceef0;
  border-radius: 999px;
  overflow: hidden;
}
.vital-bar-fill {
  height: 100%;
  background: var(--primary);
  border-radius: 999px;
  max-width: 100%;
}
.vital-row:nth-child(3n) .vital-bar-fill {
  background: var(--secondary);
}
.vital-row:nth-child(5n) .vital-bar-fill {
  background: #bc4800;
}

.budget-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}
@media (min-width: 768px) {
  .budget-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}
.budget-card {
  background: var(--surface-low);
  border-radius: 20px;
  padding: 24px;
  min-height: 140px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 8px;
  position: relative;
}
.budget-ico {
  color: var(--primary) !important;
  font-size: 28px !important;
}
.budget-label {
  font-size: 0.875rem;
  color: var(--on-variant);
}
.budget-val {
  font-size: 1.25rem;
  font-weight: 900;
}

.insights-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 48px;
}
@media (min-width: 768px) {
  .insights-grid {
    grid-template-columns: 1fr 1fr;
  }
}
.insight-heading {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1.15rem;
  font-weight: 900;
  text-transform: uppercase;
  margin: 0 0 20px;
}
.ico-circle {
  width: 32px;
  height: 32px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.ico-circle.ok {
  background: var(--secondary-container);
  color: var(--secondary);
}
.ico-circle.bad {
  background: var(--error-container);
  color: var(--error);
}
.insight-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.insight-item {
  display: flex;
  gap: 14px;
  padding: 16px;
  border-radius: 16px;
  margin-bottom: 8px;
  transition: background 0.15s;
  position: relative;
}
.insight-item:hover {
  background: var(--surface-lowest);
}
.insight-idx {
  font-weight: 800;
  color: var(--secondary);
  flex-shrink: 0;
}
.insight-idx.bad {
  color: var(--error);
}
.insight-body {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.5;
  color: var(--on-surface);
}

.profile-cards {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  margin-top: 40px;
}
@media (min-width: 768px) {
  .profile-cards {
    grid-template-columns: 1fr 1fr;
  }
}
.profile-card {
  padding: 32px;
  border-radius: 24px;
  position: relative;
}
.profile-card.warn {
  background: var(--tertiary-fixed);
  color: var(--on-tertiary-fixed);
  border-left: 8px solid #943700;
}
.profile-card.tip {
  background: var(--primary-fixed);
  color: var(--on-primary-fixed);
  border-left: 8px solid var(--primary);
}
.profile-card h4 {
  margin: 0 0 12px;
  font-size: 0.75rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  display: flex;
  align-items: center;
  gap: 8px;
}
.profile-card p {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.55;
  opacity: 0.88;
}

.nb-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.nb-card {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 24px;
  background: var(--surface-low);
  border-radius: 20px;
  cursor: default;
  transition: background 0.15s;
  position: relative;
}
.nb-card:hover {
  background: #1a2e48;
}
.nb-thumb {
  width: 96px;
  height: 96px;
  border-radius: 14px;
  overflow: hidden;
  flex-shrink: 0;
  background: #1e3352;
}
.nb-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.nb-body h4 {
  margin: 0 0 6px;
  font-weight: 900;
  font-size: 1.05rem;
}
.nb-body p {
  margin: 0;
  font-size: 0.875rem;
  color: var(--on-variant);
}
.nb-arrow {
  color: var(--outline);
  flex-shrink: 0;
}
.nb-edit {
  position: static;
  margin-left: auto;
}

.climate-section {
  background: var(--surface-lowest);
  border-radius: 28px;
  padding: 32px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}
.climate-head {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 28px;
}
.climate-ico {
  padding: 12px;
  background: rgba(0, 74, 198, 0.1);
  border-radius: 999px;
  color: var(--primary) !important;
  font-size: 28px !important;
}
.climate-sub {
  margin: 4px 0 0;
  font-size: 0.875rem;
  color: var(--on-variant);
}
.climate-chart {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 4px;
  padding-top: 12px;
  margin-top: 8px;
}
.climate-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  min-width: 0;
}
/* Altura fixa: % no filho não funcionava com flex:1 sem height explícita */
.climate-bar-area {
  width: 100%;
  height: 208px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  position: relative;
  flex-shrink: 0;
}
.climate-temp {
  font-size: 0.58rem;
  font-weight: 800;
  color: var(--on-variant);
  line-height: 1.1;
  margin-bottom: 5px;
  flex-shrink: 0;
  text-align: center;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
}
.climate-temp.on {
  color: var(--primary);
  font-size: 0.62rem;
}
@media (min-width: 768px) {
  .climate-temp {
    font-size: 0.62rem;
  }
  .climate-temp.on {
    font-size: 0.68rem;
  }
}
.climate-bar {
  width: 100%;
  flex-shrink: 0;
  background: var(--surface-low);
  border-radius: 8px 8px 0 0;
  transition: height 0.25s ease;
  box-sizing: border-box;
}
.climate-bar.on {
  background: var(--primary);
}
.climate-mon {
  font-size: 0.55rem;
  font-weight: 900;
  text-transform: uppercase;
  color: var(--outline);
}
.climate-mon.on {
  color: var(--primary);
}

.travel-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}
@media (min-width: 768px) {
  .travel-row {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }
}
@media (min-width: 1024px) {
  .travel-row {
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
  }
}
.travel-card {
  background: var(--surface-low);
  border-radius: 20px;
  padding: 24px;
  display: flex;
  gap: 14px;
  align-items: flex-start;
  position: relative;
  min-width: 0;
}
.travel-card > div {
  min-width: 0;
  flex: 1 1 auto;
}
.travel-card .ep-btn {
  flex-shrink: 0;
  align-self: flex-start;
}
.travel-ico {
  color: var(--primary) !important;
  font-size: 28px !important;
}
.travel-label {
  font-size: 0.65rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--on-variant);
}
.travel-val {
  font-size: 1.1rem;
  font-weight: 800;
  margin-top: 4px;
  line-height: 1.35;
  overflow-wrap: anywhere;
}
.travel-note {
  font-size: 0.75rem;
  color: var(--outline);
  margin-top: 4px;
  line-height: 1.45;
  overflow-wrap: anywhere;
}

.city-visa-section .section-title.tight,
.city-arrival-section .section-title.tight {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
}
.city-visa-head,
.city-arrival-head {
  margin-bottom: 0;
}
.city-visa-head .section-title,
.city-arrival-head .section-title {
  margin: 0;
}
.city-visa-ico,
.city-arrival-ico {
  font-size: 28px !important;
  color: var(--primary);
}
.city-visa-sub {
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--on-variant);
  margin: 20px 0 10px;
}
.city-visa-steps {
  margin: 0 0 16px;
  padding-left: 1.25rem;
  font-size: 0.9rem;
  line-height: 1.55;
  color: var(--on-surface);
}
.city-visa-steps li {
  margin-bottom: 8px;
}
.city-visa-meta {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 0.875rem;
  color: var(--on-variant);
  margin: 0 0 10px;
}
.city-visa-meta-ico {
  font-size: 20px !important;
  flex-shrink: 0;
  color: var(--primary);
}
.city-visa-cta {
  display: inline-flex;
  margin-top: 12px;
  text-decoration: none;
  padding: 12px 22px;
  border-radius: 12px;
  font-weight: 700;
  font-size: 0.875rem;
}
.city-arrival-block {
  margin-top: 20px;
}
.city-arrival-block:first-of-type {
  margin-top: 12px;
}
.city-arrival-subtitle {
  font-size: 0.7rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--on-variant);
  margin: 0 0 12px;
}
.city-arrival-list {
  margin: 0;
  padding-left: 1.2rem;
  font-size: 0.9rem;
  line-height: 1.55;
  color: var(--on-surface);
}
.city-arrival-list li {
  margin-bottom: 8px;
}

.muted {
  color: var(--on-variant);
  font-size: 0.875rem;
}
.hint-muted {
  font-size: 0.8rem;
  color: var(--outline);
  margin-top: 12px;
}

.city-footer {
  border-top: 1px solid #1e3352;
  background: #081526;
  padding: 48px 0 100px;
}
.city-footer-inner {
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: center;
  text-align: center;
}
@media (min-width: 768px) {
  .city-footer-inner {
    flex-direction: row;
    justify-content: space-between;
    text-align: left;
  }
}
.muted-logo {
  color: #94adc8;
}
.footer-links {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: center;
  justify-content: center;
}
.footer-links a {
  color: #94adc8;
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 600;
}
.footer-copy {
  font-size: 0.8rem;
  color: #4a6080;
}

.sticky-cta {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 60;
  background: rgba(10, 20, 38, 0.92);
  backdrop-filter: blur(16px);
  border-top: 1px solid rgba(255, 255, 255, 0.07);
  padding: 16px 0;
}
.sticky-inner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}
.sticky-text {
  flex: 1 1 200px;
  min-width: 0;
}
.sticky-line {
  margin: 0;
  font-weight: 800;
  font-size: 0.9rem;
}
.sticky-sub {
  margin: 4px 0 0;
  font-size: 0.75rem;
  color: var(--on-variant);
}
.sticky-btn {
  padding: 14px 22px;
  border: none;
  border-radius: 14px;
  font-weight: 900;
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  cursor: pointer;
  flex: 1 1 auto;
}
.sticky-btn.secondary {
  background: #e6e8ea;
  color: var(--on-surface);
}
.sticky-btn.primary {
  background: var(--primary);
  color: var(--on-primary);
  box-shadow: 0 8px 24px rgba(0, 74, 198, 0.25);
}

/* Inline edit */
.ep-anchor {
  position: relative;
}
.ep-btn {
  font-size: 0.65rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 4px 8px;
  margin-left: 8px;
  border: none;
  border-radius: 6px;
  background: rgba(0, 74, 198, 0.12);
  color: var(--primary);
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s;
  vertical-align: middle;
}
.edit-mode .ep-btn {
  opacity: 1;
}
.edit-mode .ep-anchor:hover .ep-btn {
  opacity: 1;
}
.ep-btn-block {
  display: block;
  margin: 8px 0 0;
  width: 100%;
  opacity: 1;
}
.ep-input,
.ep-textarea {
  font: inherit;
  border: 1px solid #c3c6d7;
  border-radius: 8px;
  padding: 8px 10px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  background: var(--surface-lowest);
}
.ep-input-inline {
  width: auto;
  min-width: 120px;
}
.ep-input-full {
  max-width: 520px;
}
.title-input {
  font-size: clamp(1.5rem, 5vw, 3rem);
  font-weight: 900;
  width: 100%;
}
.ep-block {
  margin-top: 12px;
  padding: 16px;
  background: var(--surface-low);
  border-radius: 12px;
  max-width: 560px;
}
.ep-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 700;
  margin: 8px 0 4px;
  color: var(--on-variant);
}
.btn-ghost {
  background: transparent;
  border: 1px solid #c3c6d7;
  border-radius: 8px;
  padding: 8px 14px;
  font-weight: 700;
  cursor: pointer;
  color: var(--on-surface);
}
.btn-primary {
  background: var(--primary);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-weight: 800;
  cursor: pointer;
}
.btn-small {
  font-size: 0.75rem;
  padding: 6px 12px;
  margin-top: 8px;
}
.ep-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}
.material-symbols-outlined {
  font-family: "Material Symbols Outlined", sans-serif;
  font-weight: 400;
  font-style: normal;
  font-size: 24px;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  display: inline-block;
  white-space: nowrap;
  word-wrap: normal;
  direction: ltr;
  font-variation-settings: "FILL" 0, "wght" 400, "GRAD" 0, "opsz" 24;
}
.ico-circle .material-symbols-outlined {
  font-variation-settings: "FILL" 1, "wght" 400, "GRAD" 0, "opsz" 24;
  font-size: 18px !important;
}
.custom-section-body {
  line-height: 1.7;
  color: var(--text);
}
.custom-section-body h2,
.custom-section-body h3 {
  font-weight: 600;
  margin: 1rem 0 0.5rem;
}
.custom-section-body p { margin: 0.5rem 0; }
.custom-section-body ul,
.custom-section-body ol { padding-left: 1.5rem; margin: 0.5rem 0; }
.custom-section-body a { color: var(--accent, #5e6ad2); text-decoration: underline; }
.custom-section-body img { max-width: 100%; border-radius: 8px; margin: 0.5rem 0; }

/* KSAMIL SCOPED CUSTOM STYLES */
.ksamil-custom-section {
  --ksamil-bg-cream: var(--surface-low);
  --ksamil-navy-primary: var(--on-surface);
  --ksamil-navy-dark: var(--on-surface);
  --ksamil-accent-terracotta: #D4A373;
  --ksamil-accent-terracotta-dark: #E7A977;
  --ksamil-accent-turquoise: #4A9090;
  --ksamil-accent-turquoise-light: rgba(74, 144, 144, 0.12);
  --ksamil-text-main: var(--on-surface);
  --ksamil-text-muted: var(--on-variant);
  --ksamil-border-color: var(--outline);
  --ksamil-white: var(--surface-lowest);
  --ksamil-siena-warning: #E76F51;
  --ksamil-siena-bg: rgba(231, 111, 81, 0.12);
  --ksamil-shadow-sm: 0 4px 12px rgba(0, 0, 0, 0.15);
  --ksamil-shadow-md: 0 8px 24px rgba(0, 0, 0, 0.25);
  --ksamil-shadow-lg: 0 16px 40px rgba(0, 0, 0, 0.35);
  --ksamil-radius-sm: 8px;
  --ksamil-radius-md: 16px;
  --ksamil-radius-lg: 24px;

  background-color: var(--ksamil-white);
  border-radius: var(--ksamil-radius-lg);
  padding: 45px;
  margin-bottom: 40px;
  box-shadow: var(--ksamil-shadow-md);
  border: 1px solid var(--ksamil-border-color);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}
.ksamil-custom-section:hover {
  box-shadow: var(--ksamil-shadow-lg);
}
.ksamil-section-header {
  margin-bottom: 20px;
  border-bottom: 2px solid var(--ksamil-border-color);
  padding-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
}
.ksamil-section-ico {
  font-size: 1.8rem;
  color: var(--ksamil-accent-terracotta);
}
.ksamil-section-subtitle {
  font-size: 0.95rem;
  color: var(--ksamil-text-muted);
  margin-top: -10px;
  margin-bottom: 20px;
  font-weight: 500;
}
.ksamil-helper-text {
  font-size: 0.9rem;
  color: var(--ksamil-accent-turquoise);
  font-weight: 700;
  text-align: center;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}
.ksamil-sub-title {
  font-size: 1.35rem;
  color: var(--ksamil-navy-primary);
  margin-top: 30px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.ksamil-sub-title .material-symbols-outlined {
  color: var(--ksamil-accent-terracotta);
  font-size: 1.5rem;
}

/* FLOWCHART WRAPPER & GRID */
.ksamil-flowchart-wrapper {
  width: 100%;
  overflow-x: auto;
  margin: 30px 0;
  border-radius: var(--ksamil-radius-md);
  border: 1px solid var(--ksamil-border-color);
  background-color: var(--ksamil-bg-cream);
  padding: 25px 20px;
  box-shadow: var(--ksamil-shadow-sm);
}
.ksamil-flowchart-grid {
  display: grid;
  grid-template-columns: 130px repeat(4, 1fr);
  grid-template-rows: 50px minmax(200px, auto) minmax(180px, auto) minmax(200px, auto);
  gap: 20px;
  position: relative;
  min-width: 960px;
}
.flowchart-col-header {
  text-align: center;
  font-size: 0.95rem;
  font-weight: 800;
  color: var(--ksamil-navy-primary);
  border-bottom: 2.5px solid var(--ksamil-accent-terracotta);
  display: flex;
  align-items: center;
  justify-content: center;
  padding-bottom: 8px;
}
.axis-timeline-column {
  grid-row: 2 / span 3;
  grid-column: 1;
  position: relative;
  height: 100%;
  width: 100%;
  padding-right: 15px;
}
.axis-vertical-line {
  position: absolute;
  right: 6px;
  top: 10px;
  bottom: 10px;
  width: 3px;
  background-color: var(--ksamil-accent-terracotta);
  border-radius: 10px;
}
.axis-node-point {
  position: absolute;
  right: 0;
  transform: translateY(-50%);
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  gap: 10px;
  text-align: right;
}
.axis-node-dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background-color: var(--ksamil-white);
  border: 3.5px solid var(--ksamil-accent-terracotta);
  z-index: 2;
  box-shadow: var(--ksamil-shadow-sm);
}
.axis-node-text {
  font-size: 0.75rem;
  font-weight: 800;
  color: var(--ksamil-navy-primary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 1.25;
  white-space: nowrap;
}
.axis-node-text small {
  font-size: 0.65rem;
  color: var(--ksamil-text-muted);
  font-weight: 600;
  display: block;
}
.flowchart-card {
  background-color: var(--ksamil-white);
  border: 1.5px solid var(--ksamil-border-color);
  border-radius: var(--ksamil-radius-md);
  padding: 16px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  transition: all 0.3s;
  position: relative;
  box-shadow: var(--ksamil-shadow-sm);
}
.flowchart-card:hover {
  border-color: var(--ksamil-accent-turquoise);
  background-color: var(--surface);
  transform: translateY(-2px);
  box-shadow: var(--ksamil-shadow-md);
}
.flowchart-card.span-vertical {
  height: 100%;
}
.flowchart-card-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--ksamil-navy-primary);
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.flowchart-card-title .material-symbols-outlined {
  color: var(--ksamil-accent-turquoise);
  font-size: 1.2rem;
}
.flowchart-card-desc {
  font-size: 0.82rem;
  color: var(--ksamil-text-main);
  line-height: 1.4;
  margin-bottom: 12px;
}
.flowchart-card-meta {
  font-size: 0.78rem;
  color: var(--ksamil-text-muted);
  font-weight: 600;
  border-top: 1px dashed var(--ksamil-border-color);
  padding-top: 8px;
  margin-top: auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.flowchart-card-price {
  color: var(--ksamil-accent-terracotta-dark);
  font-weight: 800;
  font-size: 0.95rem;
}
.or-divider-overlay {
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;
  z-index: 5;
}
.or-divider-badge {
  background-color: var(--ksamil-navy-primary);
  color: var(--ksamil-white);
  font-size: 0.75rem;
  font-weight: 800;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 2px solid var(--ksamil-white);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  pointer-events: auto;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.choice-map-thumb {
  display: inline-block;
  height: 100px;
  margin-top: 8px;
  margin-bottom: 12px;
  overflow: hidden;
  cursor: zoom-in;
  position: relative;
  border-radius: var(--ksamil-radius-sm);
  border: 1px solid var(--ksamil-border-color);
  max-width: 100%;
  align-self: center;
}
.choice-map-thumb img {
  height: 100%;
  width: auto;
  max-width: 100%;
  display: block;
  transition: transform 0.3s;
}
.choice-map-thumb:hover img {
  transform: scale(1.05);
}
.thumb-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(28, 46, 66, 0.25);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity 0.3s;
  color: var(--ksamil-white);
  font-size: 0.7rem;
  font-weight: 700;
  backdrop-filter: blur(1.5px);
}
.choice-map-thumb:hover .thumb-overlay {
  opacity: 1;
}
.thumb-overlay .material-symbols-outlined {
  font-size: 1.2rem;
  margin-bottom: 2px;
}
.ksamil-btn-map-tip {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background-color: rgba(74, 144, 144, 0.1);
  border: 1px solid var(--ksamil-accent-turquoise);
  color: #64b5b5;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  width: 100%;
  margin-top: 10px;
  transition: all 0.3s;
}
.ksamil-btn-map-tip:hover {
  background-color: var(--ksamil-accent-turquoise);
  color: var(--ksamil-white);
}
.ksamil-whatsapp-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background-color: rgba(74, 144, 144, 0.1);
  border: 1px solid var(--ksamil-accent-turquoise);
  color: #64b5b5;
  padding: 8px 12px;
  border-radius: 20px;
  text-decoration: none;
  font-size: 0.75rem;
  font-weight: 700;
  width: 100%;
  transition: all 0.3s;
}
.ksamil-whatsapp-btn:hover {
  background-color: var(--ksamil-accent-turquoise);
  color: var(--ksamil-white);
}
.ksamil-timetable-details {
  margin-top: 10px;
  font-size: 0.75rem;
  border: 1px solid var(--ksamil-border-color);
  border-radius: var(--ksamil-radius-sm);
  background-color: var(--ksamil-white);
  overflow: hidden;
  text-align: left;
  width: 100%;
}
.ksamil-timetable-details summary {
  padding: 8px 12px;
  font-weight: 700;
  cursor: pointer;
  color: var(--ksamil-navy-primary);
  display: flex;
  align-items: center;
  justify-content: space-between;
  user-select: none;
  list-style: none;
}
.ksamil-timetable-details summary::-webkit-details-marker {
  display: none;
}
.ksamil-timetable-details summary .material-symbols-outlined {
  font-size: 1rem;
}
.ksamil-timetable-details summary .dropdown-arrow {
  transition: transform 0.2s;
}
.ksamil-timetable-details[open] summary .dropdown-arrow {
  transform: rotate(180deg);
}
.ksamil-timetable-content {
  padding: 10px;
  border-top: 1px solid var(--ksamil-border-color);
  background-color: var(--ksamil-bg-cream);
}
.ksamil-timetable-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  text-align: center;
}
.ksamil-timetable-header {
  font-weight: 700;
  font-size: 0.7rem;
  color: var(--ksamil-accent-terracotta-dark);
  border-bottom: 1px solid var(--ksamil-border-color);
  padding-bottom: 2px;
  margin-bottom: 4px;
}
.ksamil-timetable-times {
  font-size: 0.75rem;
  line-height: 1.4;
  color: var(--ksamil-text-main);
  font-weight: 600;
}
.flowchart-card-stage {
  display: none;
}

/* DISCLAIMER & INFO BOXES */
.ksamil-disclaimer-box {
  border-radius: var(--ksamil-radius-md);
  padding: 22px;
  margin: 25px 0;
  box-shadow: var(--ksamil-shadow-sm);
  border-left: 5px solid;
}
.ksamil-disclaimer-box.secondary {
  background-color: rgba(212, 163, 115, 0.1);
  border-left-color: var(--ksamil-accent-terracotta);
}
.ksamil-disclaimer-box.warning {
  background-color: var(--ksamil-siena-bg);
  border-left-color: var(--ksamil-siena-warning);
}
.ksamil-disclaimer-box.turquoise {
  background-color: var(--ksamil-accent-turquoise-light);
  border-left-color: var(--ksamil-accent-turquoise);
}
.ksamil-disclaimer-box .disclaimer-header {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 700;
  font-size: 1.05rem;
  margin-bottom: 8px;
}
.ksamil-disclaimer-box.secondary .disclaimer-header {
  color: var(--ksamil-accent-terracotta-dark);
}
.ksamil-disclaimer-box.warning .disclaimer-header {
  color: var(--ksamil-siena-warning);
}
.ksamil-disclaimer-box.turquoise .disclaimer-header {
  color: var(--ksamil-accent-turquoise);
}
.ksamil-disclaimer-box .disclaimer-body {
  font-size: 0.92rem;
  margin-bottom: 0;
  line-height: 1.5;
  color: var(--ksamil-text-main);
}
.ksamil-disclaimer-box.secondary .disclaimer-body {
  color: var(--ksamil-navy-primary);
}
.ksamil-disclaimer-box.warning .disclaimer-body {
  color: var(--ksamil-text-main);
}
.ksamil-disclaimer-box.turquoise .disclaimer-body {
  color: var(--ksamil-text-main);
}

.ksamil-info-card-box {
  background-color: var(--ksamil-accent-turquoise-light);
  border-left: 5px solid var(--ksamil-accent-turquoise);
  border-radius: var(--ksamil-radius-md);
  padding: 22px;
  margin: 25px 0;
}
.ksamil-info-card-box.highlight {
  border-left-color: var(--ksamil-accent-terracotta);
  background-color: rgba(212, 163, 115, 0.1);
}
.ksamil-info-card-box .info-card-header {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 700;
  font-size: 1.05rem;
  margin-bottom: 8px;
}
.ksamil-info-card-box:not(.highlight) .info-card-header {
  color: var(--ksamil-accent-turquoise);
}
.ksamil-info-card-box.highlight .info-card-header {
  color: var(--ksamil-accent-terracotta-dark);
  font-size: 1.25rem;
}
.ksamil-info-card-box .info-card-body {
  font-size: 0.92rem;
  margin-bottom: 0;
  line-height: 1.5;
  color: var(--ksamil-text-main);
}
.ksamil-info-card-box:not(.highlight) .info-card-body {
  color: var(--ksamil-text-main);
}
.ksamil-info-card-box.highlight .info-card-body {
  color: var(--ksamil-navy-primary);
  font-size: 1rem;
}

/* FEATURES GRID */
.ksamil-features-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 25px;
  margin: 25px 0;
}
.ksamil-feature-card {
  background-color: var(--ksamil-bg-cream);
  border: 1px solid var(--ksamil-border-color);
  border-radius: var(--ksamil-radius-md);
  padding: 22px;
  display: flex;
  flex-direction: column;
}
.ksamil-feature-card .feature-card-title {
  font-size: 1.2rem;
  color: var(--ksamil-navy-primary);
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
}
.ksamil-feature-card .feature-card-title .material-symbols-outlined {
  color: var(--ksamil-accent-terracotta);
}
.ksamil-feature-card p {
  font-size: 0.95rem;
  line-height: 1.5;
  margin-bottom: 0;
  color: var(--ksamil-text-muted);
}

/* UNCOPPED IMAGE / MAP CONTAINERS */
.ksamil-map-container {
  background-color: var(--ksamil-bg-cream);
  border: 1px solid var(--ksamil-border-color);
  border-radius: var(--ksamil-radius-md);
  padding: 15px;
  margin: 25px 0;
  box-shadow: var(--ksamil-shadow-sm);
  text-align: center;
}
.ksamil-map-image-wrapper {
  position: relative;
  display: inline-block;
  max-width: 100%;
  border-radius: var(--ksamil-radius-sm);
  overflow: hidden;
  cursor: zoom-in;
  border: 1px solid var(--ksamil-border-color);
}
.ksamil-map-image-wrapper img {
  display: block;
  max-width: 100%;
  height: auto;
  max-height: 480px;
  object-fit: contain;
  transition: transform 0.5s ease;
}
.ksamil-map-image-wrapper:hover img {
  transform: scale(1.02);
}
.ksamil-map-overlay-trigger {
  position: absolute;
  bottom: 12px;
  right: 12px;
  background-color: rgba(15, 29, 44, 0.85);
  color: var(--ksamil-white);
  border: none;
  padding: 8px 14px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 6px;
  backdrop-filter: blur(4px);
  pointer-events: none;
}
.ksamil-map-overlay-trigger .material-symbols-outlined {
  font-size: 1rem;
}
.ksamil-map-caption {
  margin-top: 12px;
  font-size: 0.85rem;
  color: var(--ksamil-text-muted);
  text-align: center;
  font-style: italic;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}
.ksamil-map-caption .material-symbols-outlined {
  font-size: 1.1rem;
}

/* RESPONSIVE LAYOUT (TABLET & MOBILE) */
@media (max-width: 1024px) {
  .ksamil-features-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
}

@media (max-width: 768px) {
  .ksamil-custom-section {
    padding: 30px 15px;
    margin-bottom: 25px;
    border-radius: var(--ksamil-radius-md);
  }
  .ksamil-flowchart-wrapper {
    overflow-x: visible;
    border: none;
    box-shadow: none;
    background-color: transparent;
    padding: 0;
    margin: 20px 0;
  }
  .ksamil-flowchart-grid {
    display: flex !important;
    flex-direction: column !important;
    gap: 15px !important;
    min-width: 0 !important;
  }
  .flowchart-col-header,
  .axis-timeline-column,
  .or-divider-overlay,
  .or-divider-badge {
    display: none !important;
  }
  .flowchart-card {
    width: 100% !important;
    height: auto !important;
    padding: 16px !important;
    margin: 0 !important;
  }
  .choice-map-thumb {
    display: block !important;
    height: auto !important;
    width: 100% !important;
    max-width: 100% !important;
    margin: 10px auto 0 auto !important;
  }
  .choice-map-thumb img {
    height: auto !important;
    width: 100% !important;
    max-height: 200px !important;
  }
  .flowchart-card-stage {
    display: inline-block !important;
    font-size: 0.68rem !important;
    font-weight: 800 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.8px !important;
    color: var(--ksamil-navy-primary) !important;
    background-color: var(--ksamil-accent-turquoise-light) !important;
    padding: 4px 10px !important;
    border-radius: 4px !important;
    margin-bottom: 10px !important;
    align-self: flex-start !important;
    border-left: 3px solid var(--ksamil-accent-turquoise) !important;
  }
}
</style>

<style>
/* Teleport para document.body: garante contraste e área clicável do Salvar */
.city-modal-save-btn {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 10px 22px !important;
  background: #004ac6 !important;
  color: #fff !important;
  border: none !important;
  border-radius: 10px !important;
  font-weight: 800 !important;
  font-size: 0.875rem !important;
  cursor: pointer;
  box-sizing: border-box;
}
.city-modal-save-btn:hover:not(:disabled) {
  filter: brightness(1.06);
}
.city-modal-save-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

/* KSAMIL LIGHTBOX MODAL (GLOBAL TO WORK WITH TELEPORT) */
.ksamil-lightbox-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(15, 29, 44, 0.95);
  z-index: 99999;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  animation: ksamilFadeIn 0.3s ease;
}
.ksamil-lightbox-close {
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  color: #FFFFFF;
  font-size: 3rem;
  cursor: pointer;
  transition: transform 0.3s;
  line-height: 1;
}
.ksamil-lightbox-close:hover {
  transform: scale(1.1) rotate(90deg);
}
.ksamil-lightbox-container {
  max-width: 90%;
  max-height: 75%;
  overflow: auto;
  border-radius: 16px;
  box-shadow: 0 20px 50px rgba(0,0,0,0.5);
  background-color: #FFFFFF;
  display: flex;
  justify-content: center;
  align-items: center;
}
.ksamil-lightbox-img {
  display: block;
  max-width: 100%;
  max-height: 75vh;
  object-fit: contain;
  margin: 0 auto;
  transition: transform 0.3s ease;
}
.ksamil-lightbox-caption {
  color: #FFFFFF;
  font-size: 1rem;
  margin-top: 15px;
  text-align: center;
  max-width: 80%;
  font-weight: 600;
}
.ksamil-lightbox-controls {
  display: flex;
  gap: 12px;
  margin-top: 12px;
}
.ksamil-lightbox-ctrl-btn {
  background-color: rgba(255, 255, 255, 0.15);
  color: #FFFFFF;
  border: none;
  padding: 8px 18px;
  border-radius: 30px;
  font-weight: 700;
  cursor: pointer;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background-color 0.3s;
}
.ksamil-lightbox-ctrl-btn:hover {
  background-color: #D4A373;
}
.ksamil-lightbox-ctrl-btn .material-symbols-outlined {
  font-size: 1.1rem;
}
@keyframes ksamilFadeIn {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
